import React, { useState, useEffect, useMemo } from "react";
import PropTypes from "prop-types";
import { makeStyles } from "@material-ui/core/styles";
import Modal from "react-modal";
import moment from "moment";
import "moment/locale/th";
import axios from "axios";

// Material-UI Components & Icons
import Grid from "components/Grid/GridContainer.js";
import Col from "components/Grid/GridItem.js";
import Card from "components/Card/Card.js";
import CardHeader from "components/Card/CardHeader.js";
import CardBody from "components/Card/CardBody.js";
import Button from "components/CustomButtons/Button.js";
import Typography from "@material-ui/core/Typography";
import TreeView from "@material-ui/lab/TreeView";
import TreeItem from "@material-ui/lab/TreeItem";
import ArrowDropDownIcon from "@material-ui/icons/ArrowDropDown";
import ArrowRightIcon from "@material-ui/icons/ArrowRight";
import AccountBalanceIcon from "@material-ui/icons/AccountBalance";
import GroupWorkIcon from "@material-ui/icons/GroupWork";
import DomainIcon from "@material-ui/icons/Domain";

// API Service
import { KpiService } from "_services/kpi.service";

const styles = {
  cardTitleWhite: {
    color: "#FFFFFF",
    marginTop: "0px",
    minHeight: "auto",
    fontWeight: "500",
    fontFamily: '"Sarabun", sans-serif',
    marginBottom: "3px"
  },
  modalOverlay: {
    backgroundColor: "rgba(0,0,0,0.65)",
    zIndex: 1050
  },
  modalContent: {
    top: "50%",
    left: "50%",
    right: "auto",
    bottom: "auto",
    marginRight: "-50%",
    transform: "translate(-50%, -50%)",
    width: "1100px",
    maxWidth: "96vw",
    maxHeight: "92vh",
    overflowY: "auto",
    padding: "25px",
    borderRadius: "10px",
    fontFamily: '"Sarabun", sans-serif',
    boxShadow: "0 10px 30px rgba(0,0,0,0.3)"
  },
  rejectModalContent: {
    top: "50%",
    left: "50%",
    right: "auto",
    bottom: "auto",
    marginRight: "-50%",
    transform: "translate(-50%, -50%)",
    width: "550px",
    maxWidth: "94vw",
    overflowY: "auto",
    padding: "24px",
    borderRadius: "10px",
    fontFamily: '"Sarabun", sans-serif'
  },
  treeWrapper: {
    backgroundColor: "#ffffff",
    borderRadius: "8px",
    border: "1px solid #e2e8f0",
    boxShadow: "0 1px 3px rgba(0,0,0,0.05)",
    overflow: "hidden"
  },
  treeHeader: {
    backgroundColor: "#f8fafc",
    padding: "12px 14px",
    borderBottom: "1px solid #e2e8f0"
  },
  treeBody: {
    padding: "8px",
    maxHeight: "650px",
    overflowY: "auto"
  }
};
const useStyles = makeStyles(styles);

const SubordinateAvatar = ({ userName, fullName, hasPendingForYou }) => {
  const [imageError, setImageError] = useState(false);

  useEffect(() => {
    setImageError(false);
  }, [userName]);

  const avatarUrl = userName
    ? `https://e.trangskin.go.th/avatar/${userName}.jpg`
    : "";

  if (avatarUrl && !imageError) {
    return (
      <img
        src={avatarUrl}
        alt={fullName || userName || "avatar"}
        onError={() => setImageError(true)}
        className="rounded-circle mr-3"
        style={{
          width: "44px",
          height: "44px",
          objectFit: "cover",
          flexShrink: 0,
          boxShadow: "0 2px 4px rgba(0,0,0,0.1)"
        }}
      />
    );
  }

  return (
    <div
      className="rounded-circle d-flex align-items-center justify-content-center text-white font-weight-bold mr-3"
      style={{
        width: "44px",
        height: "44px",
        backgroundColor: hasPendingForYou ? "#f59e0b" : "#3b82f6",
        fontSize: "18px",
        flexShrink: 0
      }}
    >
      {fullName ? fullName.charAt(0) : "U"}
    </div>
  );
};

SubordinateAvatar.propTypes = {
  userName: PropTypes.string,
  fullName: PropTypes.string,
  hasPendingForYou: PropTypes.bool
};

const SubordinateAvatarPickerItem = ({ sub, isSelected, onClick }) => {
  const [imageError, setImageError] = useState(false);

  useEffect(() => {
    setImageError(false);
  }, [sub?.userName]);

  const total = sub?.totalAssignmentCount || 0;
  const pending = sub?.pendingForYouCount || 0;
  const approved = sub?.approvedCount || 0;

  const avatarUrl = sub?.userName
    ? `https://e.trangskin.go.th/avatar/${sub.userName}.jpg`
    : "";

  // Name formatting: ชื่อจริง (ชื่อเล่น)
  const realName =
    sub?.firstName ||
    (sub?.fullName ? sub.fullName.trim().split(" ")[0] : sub?.userName || "");
  const nickName = sub?.nickName ? sub.nickName.trim() : "";
  const displayName = nickName ? `${realName} (${nickName})` : realName;

  // Role / Position formatting:
  // ถ้าเป็นหัวหน้า ให้แสดงผล
  // ถ้า หัวหน้า department หัวหน้างานxxxxx
  // ถ้า หัวหน้า groupwork หัวหน้ากลุ่มงานxxxx
  const userLevelKey = sub?.userLevelKey;
  const getRoleOrPosition = () => {
    if (userLevelKey === 2) {
      const gw = (sub?.groupWorkName || "").trim();
      if (!gw) return "หัวหน้ากลุ่มงาน";
      return gw.startsWith("กลุ่มงาน")
        ? `หัวหน้า${gw}`
        : `หัวหน้ากลุ่มงาน${gw}`;
    }
    if (userLevelKey === 1) {
      const dept = (sub?.departmentName || "").trim();
      if (!dept) return "หัวหน้างาน";
      return dept.startsWith("งาน") ? `หัวหน้า${dept}` : `หัวหน้างาน${dept}`;
    }
    if (userLevelKey === 3) {
      const ms = (sub?.missionName || "").trim();
      if (!ms) return "หัวหน้าภารกิจ";
      return ms.startsWith("ภารกิจ") ? `หัวหน้า${ms}` : `หัวหน้าภารกิจ${ms}`;
    }
    return sub?.positionName || "เจ้าหน้าที่";
  };
  const roleOrPosition = getRoleOrPosition();
  const isLeader =
    userLevelKey === 1 || userLevelKey === 2 || userLevelKey === 3;

  // Badge Logic:
  // 1. ถ้าไม่มีตัวชี้วัดเลย ให้ขึ้น 0 สีแดง
  // 2. ถ้ามีตัวชี้วัดที่ยังรออนุมัติ ให้ขึ้น 3(สีแดง)/ 3(สีน้ำเงิน)
  // 3. ถ้าอนุมัติครบแล้ว ขึ้นจำนวนตัวชี้วัด (สีเขียว)
  const renderBadge = () => {
    if (total === 0) {
      return (
        <span
          className="badge badge-danger text-white shadow-sm"
          style={{
            fontSize: "12px",
            fontWeight: "bold",
            padding: "3px 8px",
            borderRadius: "12px"
          }}
          title="ยังไม่มีตัวชี้วัดที่ได้รับมอบหมาย"
        >
          0
        </span>
      );
    }

    if (pending > 0) {
      return (
        <span
          className="badge shadow-sm"
          style={{
            fontSize: "12px",
            fontWeight: "bold",
            backgroundColor: "#ffffff",
            border: "1px solid #cbd5e1",
            padding: "3px 8px",
            borderRadius: "12px"
          }}
          title={`รออนุมัติ ${pending} ข้อ / จากทั้งหมด ${total} ข้อ`}
        >
          <span style={{ color: "#dc2626", fontWeight: "bold" }}>
            {pending}
          </span>
          <span style={{ color: "#94a3b8", margin: "0 2px" }}>/</span>
          <span style={{ color: "#2563eb", fontWeight: "bold" }}>{total}</span>
        </span>
      );
    }

    if (approved === total || pending === 0) {
      return (
        <span
          className="badge badge-success text-white shadow-sm"
          style={{
            fontSize: "12px",
            fontWeight: "bold",
            padding: "3px 8px",
            borderRadius: "12px",
            backgroundColor: "#16a34a"
          }}
          title={`อนุมัติครบแล้ว ${total} ข้อ`}
        >
          <i className="fas fa-check mr-1" style={{ fontSize: "10px" }}></i>
          {total}
        </span>
      );
    }

    return (
      <span
        className="badge shadow-sm"
        style={{
          fontSize: "12px",
          fontWeight: "bold",
          backgroundColor: "#ffffff",
          border: "1px solid #cbd5e1",
          padding: "3px 8px",
          borderRadius: "12px"
        }}
      >
        <span style={{ color: "#16a34a", fontWeight: "bold" }}>{approved}</span>
        <span style={{ color: "#94a3b8", margin: "0 2px" }}>/</span>
        <span style={{ color: "#2563eb", fontWeight: "bold" }}>{total}</span>
      </span>
    );
  };

  return (
    <div
      onClick={onClick}
      role="button"
      tabIndex={0}
      className="text-center p-2 rounded"
      style={{
        cursor: "pointer",
        width: "140px",
        minWidth: "140px",
        flexShrink: 0,
        border: isSelected
          ? "2px solid #2563eb"
          : pending > 0
          ? "1px solid #fde68a"
          : "1px solid #e2e8f0",
        backgroundColor: isSelected ? "#eff6ff" : "#ffffff",
        boxShadow: isSelected
          ? "0 4px 14px rgba(37, 99, 235, 0.2)"
          : "0 1px 3px rgba(0,0,0,0.05)",
        transform: isSelected ? "translateY(-2px)" : "none",
        transition: "all 0.15s ease",
        userSelect: "none"
      }}
      title={`${displayName}\n${roleOrPosition}\nสังกัด: ${sub?.departmentName ||
        "-"}${sub?.groupWorkName ? ` (${sub.groupWorkName})` : ""}`}
    >
      <div className="position-relative d-inline-block mx-auto mb-2">
        {avatarUrl && !imageError ? (
          <img
            src={avatarUrl}
            alt={displayName}
            onError={() => setImageError(true)}
            className="rounded-circle shadow-sm"
            style={{
              width: "72px",
              height: "72px",
              objectFit: "cover",
              display: "block",
              border: isSelected
                ? "3px solid #2563eb"
                : pending > 0
                ? "3px solid #f59e0b"
                : "2px solid #cbd5e1"
            }}
          />
        ) : (
          <div
            className="rounded-circle d-flex align-items-center justify-content-center text-white font-weight-bold mx-auto shadow-sm"
            style={{
              width: "72px",
              height: "72px",
              backgroundColor: isSelected
                ? "#2563eb"
                : pending > 0
                ? "#f59e0b"
                : "#64748b",
              fontSize: "26px",
              border: isSelected ? "3px solid #1d4ed8" : "none"
            }}
          >
            {realName ? realName.charAt(0) : "U"}
          </div>
        )}

        {isSelected && (
          <span
            className="position-absolute d-flex align-items-center justify-content-center bg-primary text-white rounded-circle shadow-sm"
            style={{
              top: "-3px",
              right: "-3px",
              width: "22px",
              height: "22px",
              fontSize: "12px"
            }}
          >
            <i className="fas fa-check"></i>
          </span>
        )}
      </div>

      <div className="mb-2 d-flex justify-content-center">{renderBadge()}</div>

      <div
        className={`font-weight-bold ${
          isSelected ? "text-primary" : "text-dark"
        }`}
        style={{
          fontSize: "14px",
          lineHeight: 1.3,
          whiteSpace: "nowrap",
          overflow: "hidden",
          textOverflow: "ellipsis"
        }}
        title={displayName}
      >
        {displayName}
      </div>

      <div
        style={{
          fontSize: "12px",
          lineHeight: 1.25,
          marginTop: "3px",
          display: "-webkit-box",
          WebkitLineClamp: 2,
          WebkitBoxOrient: "vertical",
          overflow: "hidden",
          wordBreak: "break-word",
          minHeight: "30px",
          color: isLeader ? "#0369a1" : "#64748b",
          fontWeight: isLeader ? "600" : "normal"
        }}
        title={roleOrPosition}
      >
        {roleOrPosition}
      </div>
    </div>
  );
};

SubordinateAvatarPickerItem.propTypes = {
  sub: PropTypes.object.isRequired,
  isSelected: PropTypes.bool,
  onClick: PropTypes.func
};

const useTreeItemStyles = makeStyles(theme => ({
  root: {
    color: "#334155",
    "&:hover > $content": {
      backgroundColor: "#f1f5f9"
    },
    "&:focus > $content, &$selected > $content": {
      backgroundColor: "#e0f2fe !important",
      color: "#0369a1 !important"
    }
  },
  content: {
    color: "#334155",
    borderRadius: "6px",
    padding: "4px 6px",
    marginBottom: "2px",
    fontWeight: "500",
    "$expanded > &": {
      fontWeight: "600"
    }
  },
  group: {
    marginLeft: 0,
    "& $content": {
      paddingLeft: theme.spacing(2)
    }
  },
  expanded: {},
  selected: {},
  label: {
    fontWeight: "inherit",
    color: "inherit"
  },
  labelRoot: {
    display: "flex",
    alignItems: "center",
    padding: "2px 0",
    width: "100%"
  },
  labelIcon: {
    marginRight: "6px",
    fontSize: "18px"
  },
  labelText: {
    fontWeight: "inherit",
    flexGrow: 1,
    fontSize: "13px",
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis"
  },
  labelBadge: {
    fontSize: "10px",
    fontWeight: "bold",
    borderRadius: "8px",
    padding: "1px 5px",
    marginLeft: "4px"
  }
}));

function StyledTreeItem(props) {
  const classes = useTreeItemStyles();
  const {
    labelText,
    labelIcon: LabelIcon,
    labelInfo,
    pendingInfo,
    onTreeClick,
    ...other
  } = props;

  return (
    <TreeItem
      onLabelClick={onTreeClick}
      label={
        <div className={classes.labelRoot} title={labelText}>
          {LabelIcon && (
            <LabelIcon color="inherit" className={classes.labelIcon} />
          )}
          <Typography variant="body2" className={classes.labelText}>
            {labelText}
          </Typography>
          {pendingInfo > 0 && (
            <span
              className={`badge badge-warning text-dark ${classes.labelBadge}`}
              title={`${pendingInfo} ข้อรอท่านอนุมัติ`}
            >
              {pendingInfo} รอ
            </span>
          )}
          {labelInfo !== undefined && labelInfo !== null && (
            <span
              className={`badge badge-light text-muted border ${classes.labelBadge}`}
              title="จำนวนเจ้าหน้าที่"
            >
              {labelInfo}
            </span>
          )}
        </div>
      }
      classes={{
        root: classes.root,
        content: classes.content,
        expanded: classes.expanded,
        selected: classes.selected,
        group: classes.group,
        label: classes.label
      }}
      {...other}
    />
  );
}

StyledTreeItem.propTypes = {
  labelText: PropTypes.string.isRequired,
  labelIcon: PropTypes.elementType,
  labelInfo: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  pendingInfo: PropTypes.number,
  onTreeClick: PropTypes.func
};

const KpiApproval = () => {
  const classes = useStyles();
  moment.locale("th");

  // Current logged-in user info
  const currentUser = JSON.parse(localStorage.getItem("currentUser") || "{}");
  const currentUserName = (
    currentUser.username ||
    currentUser.userName ||
    currentUser.name ||
    ""
  ).toLowerCase();

  // Data States
  const [periods, setPeriods] = useState([]);
  const [selectedPeriodId, setSelectedPeriodId] = useState("");
  const [subordinates, setSubordinates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // Filters & Search
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all"); // 'all', 'pending_you', 'approved', 'rejected'

  // Mission & Tree Organization States
  const [arrMission, setArrMission] = useState([]);
  const [treeFilter, setTreeFilter] = useState({
    type: "ALL",
    id: null,
    name: "ทั้งหมด"
  });
  const [selectedNodeId, setSelectedNodeId] = useState("ALL");
  const [
    selectedSubordinateUserName,
    setSelectedSubordinateUserName
  ] = useState("");

  // Modals
  const [selectedKpiForDetail, setSelectedKpiForDetail] = useState(null);
  const [rejectingItem, setRejectingItem] = useState(null);
  const [rejectRemark, setRejectRemark] = useState("");
  const [notifyScope, setNotifyScope] = useState("single"); // 'single' or 'all'
  const [activeCriteriaTab, setActiveCriteriaTab] = useState("both"); // 'both', '6m', '12m'

  useEffect(() => {
    Modal.setAppElement("body");
    fetchPeriods();
    fetchMissions();
  }, []);

  useEffect(() => {
    if (selectedPeriodId) {
      fetchSubordinatesApproval(selectedPeriodId);
    }
  }, [selectedPeriodId]);

  const fetchPeriods = async () => {
    try {
      const res = await KpiService.getAllPeriods();
      const rawData = res?.data?.data !== undefined ? res.data.data : res?.data;
      const periodList = Array.isArray(rawData) ? rawData : [];
      setPeriods(periodList);

      if (periodList.length > 0) {
        const active = KpiService.findCurrentPeriod(periodList);
        if (active) {
          setSelectedPeriodId(active.periodID || active.id || "");
        }
      }
    } catch (err) {
      console.error("Error fetching KPI periods:", err);
      setPeriods([]);
    }
  };

  const fetchMissions = async () => {
    try {
      const res = await axios.get("/api/v1/mission/");
      const rawData = res?.data?.data !== undefined ? res.data.data : res?.data;
      if (Array.isArray(rawData)) {
        setArrMission(rawData);
      }
    } catch (err) {
      console.error("Error fetching mission hierarchy:", err);
    }
  };

  const fetchSubordinatesApproval = async periodId => {
    if (!periodId) return;
    setLoading(true);
    try {
      const res = await KpiService.getSubordinatesApproval(periodId);
      const rawData = res?.data?.data !== undefined ? res.data.data : res?.data;
      const rawList = Array.isArray(rawData) ? rawData : [];
      // Exclude logged in user: cannot approve own assignments
      const list = rawList.filter(
        sub =>
          sub &&
          sub.userName &&
          (!currentUserName || sub.userName.toLowerCase() !== currentUserName)
      );
      setSubordinates(list);
    } catch (err) {
      console.error("Error fetching subordinates approval:", err);
      setSubordinates([]);
      alert(
        "เกิดข้อผิดพลาดในการโหลดข้อมูลผู้ใต้บังคับบัญชา: " +
          (err.response?.data?.message || err.message)
      );
    } finally {
      setLoading(false);
    }
  };

  // Helper formatting
  const formatThaiDate = dateStr => {
    if (!dateStr) return "-";
    const d = moment(dateStr);
    if (!d.isValid()) return "-";
    return d.add(543, "years").format("DD MMM YYYY, HH:mm น.");
  };

  // Approval Handlers
  const handleApproveOne = async (item, subordinateName) => {
    if (
      currentUserName &&
      item?.userName &&
      item.userName.toLowerCase() === currentUserName
    ) {
      alert("ท่านไม่สามารถอนุมัติตัวชี้วัดของตนเองได้");
      return;
    }

    const confirmMsg = `ยืนยันการอนุมัติตัวชี้วัด:\n"${
      item.code ? item.code + " - " : ""
    }${item.kpiName}"\nของ: ${subordinateName}\nใช่หรือไม่?`;
    if (!window.confirm(confirmMsg)) return;

    setSubmitting(true);
    try {
      await KpiService.approveAssignment(item.assignmentID);
      await fetchSubordinatesApproval(selectedPeriodId);
    } catch (err) {
      console.error("Error approving assignment:", err);
      alert(
        "ไม่สามารถอนุมัติได้: " + (err.response?.data?.message || err.message)
      );
    } finally {
      setSubmitting(false);
    }
  };

  const handleOpenRejectModal = item => {
    setRejectingItem(item);
    setRejectRemark("");
    setNotifyScope("single");
  };

  const handleConfirmReject = async () => {
    if (!rejectingItem) return;
    if (
      currentUserName &&
      rejectingItem?.userName &&
      rejectingItem.userName.toLowerCase() === currentUserName
    ) {
      alert("ท่านไม่สามารถส่งกลับแก้ไขตัวชี้วัดของตนเองได้");
      return;
    }
    if (!rejectRemark.trim()) {
      alert("กรุณาระบุเหตุผลหรือข้อเสนอแนะในการส่งกลับแก้ไข");
      return;
    }

    setSubmitting(true);
    try {
      await KpiService.rejectAssignment(rejectingItem.assignmentID, {
        remark: rejectRemark.trim(),
        notifyAllAssignees: notifyScope === "all"
      });
      setRejectingItem(null);
      setRejectRemark("");
      setNotifyScope("single");
      await fetchSubordinatesApproval(selectedPeriodId);
    } catch (err) {
      console.error("Error rejecting assignment:", err);
      alert(
        "ไม่สามารถส่งกลับแก้ไขได้: " +
          (err.response?.data?.message || err.message)
      );
    } finally {
      setSubmitting(false);
    }
  };

  const handleApproveAllForSubordinate = async (
    targetUserName,
    fullName,
    pendingCount
  ) => {
    if (
      currentUserName &&
      targetUserName &&
      targetUserName.toLowerCase() === currentUserName
    ) {
      alert("ท่านไม่สามารถอนุมัติตัวชี้วัดของตนเองได้");
      return;
    }

    const confirmMsg = `คุณต้องการ "อนุมัติตัวชี้วัดทั้งหมด" ที่รอการอนุมัติของ\n"${fullName}" (${pendingCount} ข้อ) ใช่หรือไม่?`;
    if (!window.confirm(confirmMsg)) return;

    setSubmitting(true);
    try {
      await KpiService.approveAllSubordinateAssignments(
        selectedPeriodId,
        targetUserName
      );
      alert(`อนุมัติสำเร็จเรียบร้อย (${pendingCount} ข้อ)`);
      await fetchSubordinatesApproval(selectedPeriodId);
    } catch (err) {
      console.error("Error approving all assignments:", err);
      alert(
        "เกิดข้อผิดพลาดในการอนุมัติทั้งหมด: " +
          (err.response?.data?.message || err.message)
      );
    } finally {
      setSubmitting(false);
    }
  };

  // Handle Treeview Node Selection
  const handleSelectTreeNode = (type, id, name, nodeId) => e => {
    if (e) {
      e.stopPropagation();
    }
    setTreeFilter({ type, id, name });
    setSelectedNodeId(nodeId);
  };

  const handleClearTreeFilter = () => {
    setTreeFilter({ type: "ALL", id: null, name: "ทั้งหมด" });
    setSelectedNodeId("ALL");
  };

  // Determine logged-in user's role and organizational placement
  const userOrgContext = useMemo(() => {
    const userDepts = Array.isArray(currentUser.userDepartment)
      ? currentUser.userDepartment
      : [];

    const explicitLevel = userDepts.reduce(
      (max, d) => Math.max(max, d.userLevelKey || 0),
      0
    );

    const primaryDept =
      userDepts.find(d => d.isDefault) ||
      [...userDepts].sort(
        (a, b) => (b.userLevelKey || 0) - (a.userLevelKey || 0)
      )[0] ||
      null;

    const myDeptKey = primaryDept?.departmentKey || null;

    let myMission = null;
    let myGroupWork = null;
    let myDept = null;

    if (Array.isArray(arrMission) && myDeptKey) {
      arrMission.forEach(m => {
        if (Array.isArray(m.groupWork)) {
          m.groupWork.forEach(g => {
            if (Array.isArray(g.department)) {
              g.department.forEach(d => {
                if (d.departmentKey === myDeptKey) {
                  myMission = m;
                  myGroupWork = g;
                  myDept = d;
                }
              });
            }
          });
        }
      });
    }

    // Fallback from subordinates if not found in arrMission
    if (
      !myGroupWork &&
      Array.isArray(subordinates) &&
      subordinates.length > 0
    ) {
      const firstSub = subordinates[0];
      if (firstSub.groupWorkKey || firstSub.groupWorkName) {
        myGroupWork = {
          groupWorkKey: firstSub.groupWorkKey,
          name: firstSub.groupWorkName
        };
      }
      if (firstSub.missionKey || firstSub.missionName) {
        myMission = {
          missionKey: firstSub.missionKey,
          name: firstSub.missionName
        };
      }
    }

    // Heuristic effectiveLevel if explicitLevel is 0
    let effectiveLevel = explicitLevel;
    if (
      effectiveLevel === 0 &&
      Array.isArray(subordinates) &&
      subordinates.length > 0
    ) {
      const distinctMissions = new Set(
        subordinates.map(s => s.missionKey || s.missionName).filter(Boolean)
      );
      const distinctGroups = new Set(
        subordinates.map(s => s.groupWorkKey || s.groupWorkName).filter(Boolean)
      );
      const distinctDepts = new Set(
        subordinates
          .map(s => s.departmentKey || s.departmentName)
          .filter(Boolean)
      );

      if (distinctMissions.size > 1) effectiveLevel = 4;
      else if (distinctGroups.size > 1) effectiveLevel = 3;
      else if (distinctDepts.size > 1) effectiveLevel = 2;
      else effectiveLevel = 1;
    }

    return {
      effectiveLevel,
      primaryDept,
      myDeptKey,
      myMission,
      myGroupWork,
      myDept
    };
  }, [currentUser, arrMission, subordinates]);

  const roleTitle = useMemo(() => {
    switch (userOrgContext.effectiveLevel) {
      case 3:
        return "ระดับ: หัวหน้าภารกิจ (กลุ่มงาน > หน่วยงาน)";
      case 2:
        return "ระดับ: หัวหน้ากลุ่มงาน (หน่วยงานในกลุ่มงาน)";
      case 1:
        return "ระดับ: หัวหน้าหน่วยงาน (หน่วยงานตนเอง)";
      default:
        return userOrgContext.effectiveLevel >= 4
          ? "ระดับ: ผู้บริหาร / ส่วนกลาง"
          : "ระดับ: หัวหน้างานตามสายบังคับบัญชา";
    }
  }, [userOrgContext.effectiveLevel]);

  // Statistics maps per department, groupwork, and mission
  const deptStatsMap = useMemo(() => {
    const map = new Map();
    if (!Array.isArray(subordinates)) return map;
    subordinates.forEach(s => {
      if (
        !s ||
        (currentUserName && s.userName?.toLowerCase() === currentUserName)
      )
        return;
      const keys = [];
      if (s.departmentKey) keys.push("key_" + s.departmentKey);
      if (s.departmentName)
        keys.push("name_" + s.departmentName.trim().toLowerCase());
      keys.forEach(k => {
        const cur = map.get(k) || { count: 0, pending: 0 };
        cur.count += 1;
        cur.pending += s.pendingForYouCount || 0;
        map.set(k, cur);
      });
    });
    return map;
  }, [subordinates, currentUserName]);

  const groupStatsMap = useMemo(() => {
    const map = new Map();
    if (!Array.isArray(subordinates)) return map;
    subordinates.forEach(s => {
      if (
        !s ||
        (currentUserName && s.userName?.toLowerCase() === currentUserName)
      )
        return;
      const keys = [];
      if (s.groupWorkKey) keys.push("key_" + s.groupWorkKey);
      if (s.groupWorkName)
        keys.push("name_" + s.groupWorkName.trim().toLowerCase());
      keys.forEach(k => {
        const cur = map.get(k) || { count: 0, pending: 0 };
        cur.count += 1;
        cur.pending += s.pendingForYouCount || 0;
        map.set(k, cur);
      });
    });
    return map;
  }, [subordinates, currentUserName]);

  const missionStatsMap = useMemo(() => {
    const map = new Map();
    if (!Array.isArray(subordinates)) return map;
    subordinates.forEach(s => {
      if (
        !s ||
        (currentUserName && s.userName?.toLowerCase() === currentUserName)
      )
        return;
      const keys = [];
      if (s.missionKey) keys.push("key_" + s.missionKey);
      if (s.missionName)
        keys.push("name_" + s.missionName.trim().toLowerCase());
      keys.forEach(k => {
        const cur = map.get(k) || { count: 0, pending: 0 };
        cur.count += 1;
        cur.pending += s.pendingForYouCount || 0;
        map.set(k, cur);
      });
    });
    return map;
  }, [subordinates, currentUserName]);

  const getDeptCount = (key, name) => {
    if (key && deptStatsMap.has("key_" + key))
      return deptStatsMap.get("key_" + key).count;
    if (name && deptStatsMap.has("name_" + name.trim().toLowerCase()))
      return deptStatsMap.get("name_" + name.trim().toLowerCase()).count;
    return 0;
  };
  const getDeptPending = (key, name) => {
    if (key && deptStatsMap.has("key_" + key))
      return deptStatsMap.get("key_" + key).pending;
    if (name && deptStatsMap.has("name_" + name.trim().toLowerCase()))
      return deptStatsMap.get("name_" + name.trim().toLowerCase()).pending;
    return 0;
  };

  const getGroupCount = (key, name) => {
    if (key && groupStatsMap.has("key_" + key))
      return groupStatsMap.get("key_" + key).count;
    if (name && groupStatsMap.has("name_" + name.trim().toLowerCase()))
      return groupStatsMap.get("name_" + name.trim().toLowerCase()).count;
    return 0;
  };
  const getGroupPending = (key, name) => {
    if (key && groupStatsMap.has("key_" + key))
      return groupStatsMap.get("key_" + key).pending;
    if (name && groupStatsMap.has("name_" + name.trim().toLowerCase()))
      return groupStatsMap.get("name_" + name.trim().toLowerCase()).pending;
    return 0;
  };

  const getMissionCount = (key, name) => {
    if (key && missionStatsMap.has("key_" + key))
      return missionStatsMap.get("key_" + key).count;
    if (name && missionStatsMap.has("name_" + name.trim().toLowerCase()))
      return missionStatsMap.get("name_" + name.trim().toLowerCase()).count;
    return 0;
  };
  const getMissionPending = (key, name) => {
    if (key && missionStatsMap.has("key_" + key))
      return missionStatsMap.get("key_" + key).pending;
    if (name && missionStatsMap.has("name_" + name.trim().toLowerCase()))
      return missionStatsMap.get("name_" + name.trim().toLowerCase()).pending;
    return 0;
  };

  // For Level 2: departments belonging to user's groupwork
  const level2Departments = useMemo(() => {
    const list = [];
    const seenDeptKeys = new Set();

    if (userOrgContext.myGroupWork?.groupWorkKey && Array.isArray(arrMission)) {
      arrMission.forEach(m => {
        if (Array.isArray(m.groupWork)) {
          const matchedG = m.groupWork.find(
            g => g.groupWorkKey === userOrgContext.myGroupWork.groupWorkKey
          );
          if (matchedG && Array.isArray(matchedG.department)) {
            matchedG.department.forEach(d => {
              if (d && !seenDeptKeys.has(d.departmentKey)) {
                seenDeptKeys.add(d.departmentKey);
                list.push({
                  departmentKey: d.departmentKey,
                  name: d.name
                });
              }
            });
          }
        }
      });
    }

    if (Array.isArray(subordinates)) {
      subordinates.forEach(s => {
        if (s.departmentName) {
          const key = s.departmentKey || s.departmentName;
          if (!seenDeptKeys.has(key)) {
            seenDeptKeys.add(key);
            list.push({
              departmentKey: s.departmentKey,
              name: s.departmentName
            });
          }
        }
      });
    }

    return list;
  }, [userOrgContext.myGroupWork, arrMission, subordinates]);

  // For Level 3: groupworks and departments in user's mission
  const level3GroupWorks = useMemo(() => {
    if (userOrgContext.myMission?.missionKey && Array.isArray(arrMission)) {
      const matchedM = arrMission.find(
        m => m.missionKey === userOrgContext.myMission.missionKey
      );
      if (matchedM && Array.isArray(matchedM.groupWork)) {
        return matchedM.groupWork;
      }
    }

    const groupMap = new Map();
    if (Array.isArray(subordinates)) {
      subordinates.forEach(s => {
        if (s.groupWorkName) {
          const gKey = s.groupWorkKey || s.groupWorkName;
          if (!groupMap.has(gKey)) {
            groupMap.set(gKey, {
              groupWorkKey: s.groupWorkKey,
              name: s.groupWorkName,
              department: []
            });
          }
          if (s.departmentName) {
            const gObj = groupMap.get(gKey);
            if (
              !gObj.department.some(
                d =>
                  (s.departmentKey && d.departmentKey === s.departmentKey) ||
                  d.name === s.departmentName
              )
            ) {
              gObj.department.push({
                departmentKey: s.departmentKey,
                name: s.departmentName
              });
            }
          }
        }
      });
    }
    return Array.from(groupMap.values());
  }, [userOrgContext.myMission, arrMission, subordinates]);

  // Render TreeView content according to hierarchical rules
  const renderOrgTree = () => {
    const {
      effectiveLevel,
      myGroupWork,
      myMission,
      myDept,
      primaryDept
    } = userOrgContext;

    // Case 1: Level 1 (หัวหน้าหน่วยงาน) -> เห็นแค่หน่วยงานเดียวกัน เท่านั้น
    if (effectiveLevel === 1) {
      const deptKey = myDept?.departmentKey || primaryDept?.departmentKey;
      const deptName =
        myDept?.name ||
        primaryDept?.name ||
        subordinates[0]?.departmentName ||
        "หน่วยงานของท่าน";
      const count = getDeptCount(deptKey, deptName);
      const pending = getDeptPending(deptKey, deptName);

      return (
        <StyledTreeItem
          nodeId="ALL"
          labelText={deptName}
          labelIcon={DomainIcon}
          labelInfo={count > 0 ? count : null}
          pendingInfo={pending}
          onTreeClick={handleSelectTreeNode("ALL", null, deptName, "ALL")}
        />
      );
    }

    // Case 2: Level 2 (หัวหน้ากลุ่มงาน) -> เห็นหน่วยงานภายในกลุ่มงานของตัวเอง (1 Level)
    if (effectiveLevel === 2) {
      const groupName =
        myGroupWork?.name ||
        subordinates[0]?.groupWorkName ||
        "กลุ่มงานของท่าน";
      const groupKey =
        myGroupWork?.groupWorkKey || subordinates[0]?.groupWorkKey;
      const groupCount = getGroupCount(groupKey, groupName) || stats.totalSubs;
      const groupPending =
        getGroupPending(groupKey, groupName) || stats.pendingForYou;

      return (
        <StyledTreeItem
          nodeId="G_ROOT"
          labelText={`ทั้งหมด (${groupName})`}
          labelIcon={GroupWorkIcon}
          labelInfo={groupCount > 0 ? groupCount : null}
          pendingInfo={groupPending}
          onTreeClick={handleSelectTreeNode(
            "ALL",
            null,
            `ทั้งหมด (${groupName})`,
            "G_ROOT"
          )}
        >
          {level2Departments.map(dept => {
            const dKey = dept.departmentKey;
            const dName = dept.name;
            const dCount = getDeptCount(dKey, dName);
            const dPending = getDeptPending(dKey, dName);
            const nodeId = "D_" + (dKey || dName);

            return (
              <StyledTreeItem
                key={nodeId}
                nodeId={nodeId}
                labelText={dName}
                labelIcon={DomainIcon}
                labelInfo={dCount > 0 ? dCount : null}
                pendingInfo={dPending}
                onTreeClick={handleSelectTreeNode("D", dKey, dName, nodeId)}
              />
            );
          })}
        </StyledTreeItem>
      );
    }

    // Case 3: Level 3 (หัวหน้าภารกิจ) -> เห็นกลุ่มงาน > หน่วยงาน (2 Level)
    if (effectiveLevel === 3) {
      const missionName =
        myMission?.name || subordinates[0]?.missionName || "ภารกิจของท่าน";
      const missionKey = myMission?.missionKey || subordinates[0]?.missionKey;
      const missionCount =
        getMissionCount(missionKey, missionName) || stats.totalSubs;
      const missionPending =
        getMissionPending(missionKey, missionName) || stats.pendingForYou;

      return (
        <StyledTreeItem
          nodeId="M_ROOT"
          labelText={`ทั้งหมด (${missionName})`}
          labelIcon={AccountBalanceIcon}
          labelInfo={missionCount > 0 ? missionCount : null}
          pendingInfo={missionPending}
          onTreeClick={handleSelectTreeNode(
            "ALL",
            null,
            `ทั้งหมด (${missionName})`,
            "M_ROOT"
          )}
        >
          {level3GroupWorks.map(g => {
            const gKey = g.groupWorkKey;
            const gName = g.name;
            const gCount = getGroupCount(gKey, gName);
            const gPending = getGroupPending(gKey, gName);
            const gNodeId = "G_" + (gKey || gName);

            return (
              <StyledTreeItem
                key={gNodeId}
                nodeId={gNodeId}
                labelText={gName}
                labelIcon={GroupWorkIcon}
                labelInfo={gCount > 0 ? gCount : null}
                pendingInfo={gPending}
                onTreeClick={handleSelectTreeNode("G", gKey, gName, gNodeId)}
              >
                {Array.isArray(g.department) &&
                  g.department.map(d => {
                    const dKey = d.departmentKey;
                    const dName = d.name;
                    const dCount = getDeptCount(dKey, dName);
                    const dPending = getDeptPending(dKey, dName);
                    const dNodeId = "D_" + (dKey || dName);

                    return (
                      <StyledTreeItem
                        key={dNodeId}
                        nodeId={dNodeId}
                        labelText={dName}
                        labelIcon={DomainIcon}
                        labelInfo={dCount > 0 ? dCount : null}
                        pendingInfo={dPending}
                        onTreeClick={handleSelectTreeNode(
                          "D",
                          dKey,
                          dName,
                          dNodeId
                        )}
                      />
                    );
                  })}
              </StyledTreeItem>
            );
          })}
        </StyledTreeItem>
      );
    }

    // Case 4: Level >= 4 (ผู้บริหาร / ผู้อำนวยการ / Central Admin) -> แสดงทั้งโรงพยาบาล
    return (
      <StyledTreeItem
        nodeId="ALL"
        labelText="ทั้งหมด (ทั้งโรงพยาบาล)"
        labelIcon={AccountBalanceIcon}
        labelInfo={stats.totalSubs > 0 ? stats.totalSubs : null}
        pendingInfo={stats.pendingForYou}
        onTreeClick={handleSelectTreeNode("ALL", null, "ทั้งหมด", "ALL")}
      >
        {Array.isArray(arrMission) &&
          arrMission.map(m => {
            const mKey = m.missionKey;
            const mName = m.name;
            const mCount = getMissionCount(mKey, mName);
            const mPending = getMissionPending(mKey, mName);
            const mNodeId = "M_" + (mKey || mName);

            return (
              <StyledTreeItem
                key={mNodeId}
                nodeId={mNodeId}
                labelText={mName}
                labelIcon={AccountBalanceIcon}
                labelInfo={mCount > 0 ? mCount : null}
                pendingInfo={mPending}
                onTreeClick={handleSelectTreeNode("M", mKey, mName, mNodeId)}
              >
                {Array.isArray(m.groupWork) &&
                  m.groupWork.map(g => {
                    const gKey = g.groupWorkKey;
                    const gName = g.name;
                    const gCount = getGroupCount(gKey, gName);
                    const gPending = getGroupPending(gKey, gName);
                    const gNodeId = "G_" + (gKey || gName);

                    return (
                      <StyledTreeItem
                        key={gNodeId}
                        nodeId={gNodeId}
                        labelText={gName}
                        labelIcon={GroupWorkIcon}
                        labelInfo={gCount > 0 ? gCount : null}
                        pendingInfo={gPending}
                        onTreeClick={handleSelectTreeNode(
                          "G",
                          gKey,
                          gName,
                          gNodeId
                        )}
                      >
                        {Array.isArray(g.department) &&
                          g.department.map(d => {
                            const dKey = d.departmentKey;
                            const dName = d.name;
                            const dCount = getDeptCount(dKey, dName);
                            const dPending = getDeptPending(dKey, dName);
                            const dNodeId = "D_" + (dKey || dName);

                            return (
                              <StyledTreeItem
                                key={dNodeId}
                                nodeId={dNodeId}
                                labelText={dName}
                                labelIcon={DomainIcon}
                                labelInfo={dCount > 0 ? dCount : null}
                                pendingInfo={dPending}
                                onTreeClick={handleSelectTreeNode(
                                  "D",
                                  dKey,
                                  dName,
                                  dNodeId
                                )}
                              />
                            );
                          })}
                      </StyledTreeItem>
                    );
                  })}
              </StyledTreeItem>
            );
          })}
      </StyledTreeItem>
    );
  };

  // Filtered Subordinates
  const filteredSubordinates = useMemo(() => {
    if (!Array.isArray(subordinates)) return [];
    return subordinates.filter(sub => {
      if (!sub) return false;
      // Do not display own record to approve self
      if (
        currentUserName &&
        sub.userName &&
        sub.userName.toLowerCase() === currentUserName
      ) {
        return false;
      }

      // Organizational Tree Filter
      if (treeFilter.type === "D") {
        const matchDept =
          (treeFilter.id && sub.departmentKey === treeFilter.id) ||
          (treeFilter.name && sub.departmentName === treeFilter.name);
        if (!matchDept) return false;
      } else if (treeFilter.type === "G") {
        const matchGroup =
          (treeFilter.id && sub.groupWorkKey === treeFilter.id) ||
          (treeFilter.name && sub.groupWorkName === treeFilter.name);
        if (!matchGroup) return false;
      } else if (treeFilter.type === "M") {
        const matchMission =
          (treeFilter.id && sub.missionKey === treeFilter.id) ||
          (treeFilter.name && sub.missionName === treeFilter.name);
        if (!matchMission) return false;
      }

      // Search term filter
      const term = searchTerm.trim().toLowerCase();
      const matchSearch =
        !term ||
        (sub.fullName && sub.fullName.toLowerCase().includes(term)) ||
        (sub.userName && sub.userName.toLowerCase().includes(term)) ||
        (sub.nickName && sub.nickName.toLowerCase().includes(term)) ||
        (sub.positionName && sub.positionName.toLowerCase().includes(term)) ||
        (sub.departmentName &&
          sub.departmentName.toLowerCase().includes(term)) ||
        (sub.groupWorkName && sub.groupWorkName.toLowerCase().includes(term));

      if (!matchSearch) return false;

      // Status filter
      if (statusFilter === "pending_you") {
        return (sub.pendingForYouCount || 0) > 0;
      }
      if (statusFilter === "approved") {
        return (sub.approvedCount || 0) > 0;
      }
      if (statusFilter === "rejected") {
        return (sub.rejectedCount || 0) > 0;
      }
      return true;
    });
  }, [subordinates, treeFilter, searchTerm, statusFilter, currentUserName]);

  // Overall Statistics (reflecting treeFilter)
  const stats = useMemo(() => {
    if (!Array.isArray(subordinates)) {
      return {
        totalSubs: 0,
        totalAssignments: 0,
        pendingForYou: 0,
        approved: 0,
        rejected: 0
      };
    }
    const validSubs = subordinates.filter(
      s =>
        s &&
        s.userName &&
        (!currentUserName || s.userName.toLowerCase() !== currentUserName) &&
        (treeFilter.type === "ALL" ||
          (treeFilter.type === "D" &&
            ((treeFilter.id && s.departmentKey === treeFilter.id) ||
              (treeFilter.name && s.departmentName === treeFilter.name))) ||
          (treeFilter.type === "G" &&
            ((treeFilter.id && s.groupWorkKey === treeFilter.id) ||
              (treeFilter.name && s.groupWorkName === treeFilter.name))) ||
          (treeFilter.type === "M" &&
            ((treeFilter.id && s.missionKey === treeFilter.id) ||
              (treeFilter.name && s.missionName === treeFilter.name))))
    );
    let totalSubs = validSubs.length;
    let totalAssignments = 0;
    let pendingForYou = 0;
    let approved = 0;
    let rejected = 0;

    validSubs.forEach(s => {
      totalAssignments += s.totalAssignmentCount || 0;
      pendingForYou += s.pendingForYouCount || 0;
      approved += s.approvedCount || 0;
      rejected += s.rejectedCount || 0;
    });

    return { totalSubs, totalAssignments, pendingForYou, approved, rejected };
  }, [subordinates, treeFilter, currentUserName]);

  // Auto-select subordinate when list changes or on first load
  useEffect(() => {
    if (
      Array.isArray(filteredSubordinates) &&
      filteredSubordinates.length > 0
    ) {
      const exists = filteredSubordinates.some(
        s => s && s.userName === selectedSubordinateUserName
      );
      if (!exists) {
        // Prefer subordinate with pending approvals, otherwise first person
        const pendingSub = filteredSubordinates.find(
          s => s && (s.pendingForYouCount || 0) > 0
        );
        setSelectedSubordinateUserName(
          pendingSub ? pendingSub.userName : filteredSubordinates[0].userName
        );
      }
    } else {
      setSelectedSubordinateUserName("");
    }
  }, [filteredSubordinates, selectedSubordinateUserName]);

  const selectedSubordinate = useMemo(() => {
    if (!selectedSubordinateUserName || !Array.isArray(filteredSubordinates)) {
      return null;
    }
    return (
      filteredSubordinates.find(
        s => s && s.userName === selectedSubordinateUserName
      ) || null
    );
  }, [filteredSubordinates, selectedSubordinateUserName]);

  const selectedPeriodObj = useMemo(() => {
    if (!Array.isArray(periods)) return null;
    return (
      periods.find(p => p && (p.periodID || p.id) === selectedPeriodId) || null
    );
  }, [periods, selectedPeriodId]);

  return (
    <div>
      {/* Header Card */}
      <Grid>
        <Col xs={12}>
          <Card>
            <CardHeader color="info">
              <div className="d-flex flex-column flex-md-row justify-content-between align-items-md-center">
                <div>
                  <h3
                    className={classes.cardTitleWhite}
                    style={{ fontWeight: "bold" }}
                  >
                    <i className="fas fa-user-check mr-2"></i>{" "}
                    อนุมัติตัวชี้วัดรายบุคคล (Individual KPI Approval)
                  </h3>
                  <p style={{ color: "#E0F2FE", margin: 0, fontSize: "14px" }}>
                    ตรวจสอบและอนุมัติตัวชี้วัดผลการปฏิบัติงานของเจ้าหน้าที่ใต้บังคับบัญชาตามสายงาน
                    (ตามลำดับขั้นบันได: หัวหน้างาน ➔ หัวหน้ากลุ่มงาน ➔
                    หัวหน้าภารกิจ)
                  </p>
                </div>
                <div className="mt-2 mt-md-0 d-flex align-items-center">
                  <div className="bg-white px-3 py-1 rounded shadow-sm d-flex align-items-center">
                    <span
                      className="text-dark font-weight-bold mr-2 text-nowrap"
                      style={{ fontSize: "13px" }}
                    >
                      รอบการประเมิน:
                    </span>
                    <select
                      className="form-control form-control-sm border-0 font-weight-bold text-primary"
                      style={{ minWidth: "220px", cursor: "pointer" }}
                      value={selectedPeriodId}
                      onChange={e => setSelectedPeriodId(e.target.value)}
                    >
                      {Array.isArray(periods) &&
                        periods.map(p => (
                          <option
                            key={p.periodID || p.id}
                            value={p.periodID || p.id}
                          >
                            {p.periodName || p.name}{" "}
                            {p.fiscalYear ? `(${p.fiscalYear})` : ""}
                          </option>
                        ))}
                    </select>
                  </div>
                  <button
                    type="button"
                    className="btn btn-outline-light btn-sm ml-2 text-nowrap"
                    onClick={() => fetchSubordinatesApproval(selectedPeriodId)}
                    title="โหลดข้อมูลใหม่"
                    disabled={loading || submitting}
                  >
                    <i
                      className={`fas fa-sync-alt ${loading ? "fa-spin" : ""}`}
                    ></i>
                  </button>
                </div>
              </div>
            </CardHeader>
            <CardBody>
              <div className="row">
                {/* Left Column: Organization TreeView Filter */}
                <div className="col-12 col-lg-3 col-xl-3 mb-4">
                  <div
                    className={classes.treeWrapper}
                    style={{ position: "sticky", top: "20px" }}
                  >
                    <div
                      className={`${classes.treeHeader} d-flex justify-content-between align-items-center`}
                    >
                      <div>
                        <div
                          className="font-weight-bold text-dark d-flex align-items-center"
                          style={{ fontSize: "14px" }}
                        >
                          <i className="fas fa-sitemap text-primary mr-2"></i>
                          <span>โครงสร้างองค์กร</span>
                        </div>
                        <div
                          className="text-muted"
                          style={{ fontSize: "11px", marginTop: "2px" }}
                        >
                          {roleTitle}
                        </div>
                      </div>
                      {treeFilter.type !== "ALL" && (
                        <button
                          type="button"
                          className="btn btn-outline-secondary btn-sm py-0 px-2"
                          style={{ fontSize: "11px", height: "24px" }}
                          onClick={handleClearTreeFilter}
                          title="ล้างตัวกรองแสดงทั้งหมด"
                        >
                          ล้าง
                        </button>
                      )}
                    </div>
                    <div className={classes.treeBody}>
                      <TreeView
                        defaultCollapseIcon={<ArrowDropDownIcon />}
                        defaultExpandIcon={<ArrowRightIcon />}
                        selected={selectedNodeId}
                        defaultExpanded={["ALL", "M_ROOT", "G_ROOT"]}
                      >
                        {renderOrgTree()}
                      </TreeView>
                    </div>
                  </div>
                </div>

                {/* Right Column: Stats, Search & Subordinate List */}
                <div className="col-12 col-lg-9 col-xl-9">
                  {/* Active Filter Notice */}
                  {treeFilter.type !== "ALL" && (
                    <div
                      className="alert alert-info py-2 px-3 mb-3 d-flex justify-content-between align-items-center shadow-sm"
                      style={{ borderRadius: "8px" }}
                    >
                      <div className="d-flex align-items-center">
                        <i className="fas fa-filter text-info mr-2"></i>
                        <span style={{ fontSize: "13px" }}>
                          กำลังกรองข้อมูลตาม:{" "}
                          <strong className="text-dark">
                            {treeFilter.name}
                          </strong>{" "}
                          <span className="badge badge-light border text-muted ml-1">
                            พบ {filteredSubordinates.length} คน
                          </span>
                        </span>
                      </div>
                      <button
                        type="button"
                        className="btn btn-link btn-sm p-0 text-info font-weight-bold"
                        style={{
                          fontSize: "13px",
                          textDecoration: "underline"
                        }}
                        onClick={handleClearTreeFilter}
                      >
                        <i className="fas fa-times-circle mr-1"></i> แสดงทั้งหมด
                      </button>
                    </div>
                  )}

                  {/* Stat summary cards */}
                  <div className="row mb-4">
                    <div className="col-6 col-md-3 mb-2">
                      <div className="p-3 bg-light rounded border text-center">
                        <div className="text-muted small font-weight-bold">
                          เจ้าหน้าที่ใต้บังคับบัญชา
                        </div>
                        <div className="h3 font-weight-bold text-dark mb-0">
                          {stats.totalSubs}{" "}
                          <span className="small text-muted font-weight-normal">
                            คน
                          </span>
                        </div>
                      </div>
                    </div>
                    <div className="col-6 col-md-3 mb-2">
                      <div
                        className="p-3 rounded border text-center"
                        style={{
                          backgroundColor: "#fff8e1",
                          borderColor: "#ffe082"
                        }}
                      >
                        <div
                          className="text-warning small font-weight-bold"
                          style={{ color: "#f57c00" }}
                        >
                          <i className="fas fa-hourglass-half mr-1"></i>{" "}
                          รอท่านอนุมัติ
                        </div>
                        <div
                          className="h3 font-weight-bold text-warning mb-0"
                          style={{ color: "#e65100" }}
                        >
                          {stats.pendingForYou}{" "}
                          <span className="small font-weight-normal">ข้อ</span>
                        </div>
                      </div>
                    </div>
                    <div className="col-6 col-md-3 mb-2">
                      <div
                        className="p-3 rounded border text-center"
                        style={{
                          backgroundColor: "#e8f5e9",
                          borderColor: "#c8e6c9"
                        }}
                      >
                        <div className="text-success small font-weight-bold">
                          <i className="fas fa-check-circle mr-1"></i>{" "}
                          อนุมัติแล้ว
                        </div>
                        <div className="h3 font-weight-bold text-success mb-0">
                          {stats.approved}{" "}
                          <span className="small font-weight-normal">ข้อ</span>
                        </div>
                      </div>
                    </div>
                    <div className="col-6 col-md-3 mb-2">
                      <div
                        className="p-3 rounded border text-center"
                        style={{
                          backgroundColor: "#ffebee",
                          borderColor: "#ffcdd2"
                        }}
                      >
                        <div className="text-danger small font-weight-bold">
                          <i className="fas fa-undo-alt mr-1"></i> ส่งกลับแก้ไข
                        </div>
                        <div className="h3 font-weight-bold text-danger mb-0">
                          {stats.rejected}{" "}
                          <span className="small font-weight-normal">ข้อ</span>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Subordinate Avatars Picker (บน Filters row) */}
                  <div className="bg-white rounded border shadow-sm p-3 mb-3">
                    <div className="d-flex justify-content-between align-items-center mb-2">
                      <div
                        className="font-weight-bold text-dark d-flex align-items-center"
                        style={{ fontSize: "14px" }}
                      >
                        <i className="fas fa-user-friends text-primary mr-2"></i>
                        <span>
                          รายชื่อเจ้าหน้าที่ในสังกัด (
                          {filteredSubordinates.length} คน)
                        </span>
                        {treeFilter.type !== "ALL" && (
                          <span className="badge badge-light border text-muted ml-2 font-weight-normal">
                            {treeFilter.name}
                          </span>
                        )}
                      </div>
                      {selectedSubordinate && (
                        <div className="small text-muted d-none d-md-block">
                          กำลังดูข้อมูล:{" "}
                          <strong className="text-primary">
                            {selectedSubordinate.fullName}
                          </strong>
                        </div>
                      )}
                    </div>

                    {filteredSubordinates.length === 0 ? (
                      <div className="text-center py-3 text-muted small">
                        ไม่พบเจ้าหน้าที่ในสังกัดตามเงื่อนไขที่เลือก
                      </div>
                    ) : (
                      <div
                        className="d-flex flex-row align-items-stretch py-2"
                        style={{
                          gap: "10px",
                          overflowX: "auto",
                          overflowY: "hidden",
                          scrollbarWidth: "thin",
                          paddingBottom: "8px"
                        }}
                      >
                        {filteredSubordinates.map(sub => (
                          <SubordinateAvatarPickerItem
                            key={sub.userName}
                            sub={sub}
                            isSelected={
                              selectedSubordinateUserName === sub.userName
                            }
                            onClick={() =>
                              setSelectedSubordinateUserName(sub.userName)
                            }
                          />
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Filters row */}
                  <div className="row mb-3 align-items-center">
                    <div className="col-md-5 mb-2 mb-md-0">
                      <div className="input-group">
                        <div className="input-group-prepend">
                          <span className="input-group-text bg-white border-right-0">
                            <i className="fas fa-search text-muted"></i>
                          </span>
                        </div>
                        <input
                          type="text"
                          className="form-control border-left-0"
                          placeholder="ค้นหาชื่อ, ตำแหน่ง, หน่วยงาน, กลุ่มงาน..."
                          value={searchTerm}
                          onChange={e => setSearchTerm(e.target.value)}
                        />
                        {searchTerm && (
                          <div className="input-group-append">
                            <button
                              className="btn btn-outline-secondary"
                              type="button"
                              onClick={() => setSearchTerm("")}
                            >
                              <i className="fas fa-times"></i>
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="col-md-7 d-flex justify-content-md-end flex-wrap">
                      <div
                        className="btn-group btn-group-toggle"
                        data-toggle="buttons"
                      >
                        <button
                          type="button"
                          className={`btn btn-sm ${
                            statusFilter === "all"
                              ? "btn-primary"
                              : "btn-outline-secondary"
                          }`}
                          onClick={() => setStatusFilter("all")}
                        >
                          ทั้งหมด ({stats.totalSubs})
                        </button>
                        <button
                          type="button"
                          className={`btn btn-sm ${
                            statusFilter === "pending_you"
                              ? "btn-warning font-weight-bold"
                              : "btn-outline-warning text-dark"
                          }`}
                          onClick={() => setStatusFilter("pending_you")}
                        >
                          <i className="fas fa-clock mr-1"></i> รอท่านอนุมัติ (
                          {stats.pendingForYou})
                        </button>
                        <button
                          type="button"
                          className={`btn btn-sm ${
                            statusFilter === "approved"
                              ? "btn-success"
                              : "btn-outline-success"
                          }`}
                          onClick={() => setStatusFilter("approved")}
                        >
                          อนุมัติแล้ว
                        </button>
                        <button
                          type="button"
                          className={`btn btn-sm ${
                            statusFilter === "rejected"
                              ? "btn-danger"
                              : "btn-outline-danger"
                          }`}
                          onClick={() => setStatusFilter("rejected")}
                        >
                          ส่งกลับแก้ไข
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Subordinates List (Full Col-12) */}
              <div className="row mt-2">
                <div className="col-12">
                  {loading ? (
                    <div className="text-center py-5">
                      <div
                        className="spinner-border text-primary"
                        role="status"
                      >
                        <span className="sr-only">กำลังโหลด...</span>
                      </div>
                      <div className="mt-2 text-muted">
                        กำลังดึงข้อมูลสายการบังคับบัญชาและตัวชี้วัด...
                      </div>
                    </div>
                  ) : filteredSubordinates.length === 0 ? (
                    <div className="text-center py-5 bg-light rounded border">
                      <i className="fas fa-user-friends fa-3x text-muted mb-3"></i>
                      <h5 className="text-muted">
                        ไม่พบข้อมูลตัวชี้วัดของผู้ใต้บังคับบัญชาในรอบนี้
                      </h5>
                      <p className="small text-muted mb-0">
                        {searchTerm || statusFilter !== "all"
                          ? "ลองปรับเงื่อนไขการค้นหาหรือตัวกรองสถานะ"
                          : "ท่านอาจไม่มีเจ้าหน้าที่ใต้บังคับบัญชาที่มีการมอบหมายตัวชี้วัดในรอบการประเมินนี้"}
                      </p>
                    </div>
                  ) : !selectedSubordinate ? (
                    <div className="text-center py-5 bg-light rounded border">
                      <i className="fas fa-hand-pointer fa-3x text-primary mb-3"></i>
                      <h5 className="text-muted">
                        กรุณาคลิกเลือกเจ้าหน้าที่จาก Avatar
                        ด้านบนเพื่อตรวจสอบตัวชี้วัด
                      </h5>
                    </div>
                  ) : (
                    <div className="selected-subordinate-container">
                      {(() => {
                        const sub = selectedSubordinate;
                        const totalWeight = sub.totalWeight || 0;
                        const isWeight100 =
                          Math.abs(totalWeight - 100.0) < 0.01;
                        const hasPendingForYou =
                          (sub.pendingForYouCount || 0) > 0;

                        return (
                          <div
                            key={sub.userName}
                            className={`card mb-3 shadow-sm border ${
                              hasPendingForYou ? "border-warning" : ""
                            }`}
                            style={{ borderRadius: "8px", overflow: "hidden" }}
                          >
                            {/* Subordinate Card Header */}
                            <div
                              className="card-header bg-white py-3"
                              style={{
                                borderBottom: "1px solid #edf2f7",
                                borderLeft: hasPendingForYou
                                  ? "5px solid #f59e0b"
                                  : "5px solid #3b82f6"
                              }}
                            >
                              <div className="row align-items-center">
                                <div className="col-lg-6 col-md-7 mb-2 mb-md-0">
                                  <div className="d-flex align-items-center">
                                    <SubordinateAvatar
                                      userName={sub.userName}
                                      fullName={sub.fullName}
                                      hasPendingForYou={hasPendingForYou}
                                    />
                                    <div>
                                      <div
                                        className="font-weight-bold text-dark"
                                        style={{ fontSize: "16px" }}
                                      >
                                        {sub.fullName}{" "}
                                        {sub.nickName && (
                                          <span className="text-muted font-weight-normal">
                                            ({sub.nickName})
                                          </span>
                                        )}
                                      </div>
                                      <div className="small text-muted">
                                        <span className="mr-2">
                                          <i className="fas fa-id-badge mr-1"></i>
                                          {sub.userLevelKey === 2
                                            ? sub.groupWorkName?.startsWith(
                                                "กลุ่มงาน"
                                              )
                                              ? `หัวหน้า${sub.groupWorkName}`
                                              : `หัวหน้ากลุ่มงาน${sub.groupWorkName ||
                                                  ""}`
                                            : sub.userLevelKey === 1
                                            ? sub.departmentName?.startsWith(
                                                "งาน"
                                              )
                                              ? `หัวหน้า${sub.departmentName}`
                                              : `หัวหน้างาน${sub.departmentName ||
                                                  ""}`
                                            : sub.positionName ||
                                              "ไม่ระบุตำแหน่ง"}
                                        </span>
                                        {sub.departmentName && (
                                          <span className="mr-2">
                                            <i className="fas fa-hospital-alt mr-1"></i>
                                            {sub.departmentName}
                                          </span>
                                        )}
                                        {sub.groupWorkName && (
                                          <span>
                                            <i className="fas fa-layer-group mr-1"></i>
                                            {sub.groupWorkName}
                                          </span>
                                        )}
                                      </div>
                                    </div>
                                  </div>
                                </div>

                                <div className="col-lg-6 col-md-5 d-flex flex-column flex-sm-row justify-content-sm-end align-items-sm-center">
                                  {/* Weights and count badges */}
                                  <div className="mr-sm-3 mb-2 mb-sm-0 text-sm-right">
                                    <div>
                                      <span
                                        className={`badge px-2 py-1 ${
                                          isWeight100
                                            ? "badge-success"
                                            : "badge-warning"
                                        }`}
                                        style={{ fontSize: "12px" }}
                                        title={
                                          !isWeight100
                                            ? "น้ำหนักรวมควรมีค่าเท่ากับ 100%"
                                            : "น้ำหนักรวมครบ 100%"
                                        }
                                      >
                                        น้ำหนักรวม: {totalWeight.toFixed(2)}%
                                        {!isWeight100 && " (ยังไม่ครบ 100%)"}
                                      </span>
                                      <span
                                        className="badge badge-light border ml-1 px-2 py-1"
                                        style={{ fontSize: "12px" }}
                                      >
                                        {sub.totalAssignmentCount || 0}{" "}
                                        ตัวชี้วัด
                                      </span>
                                    </div>
                                    <div className="mt-1 small">
                                      {hasPendingForYou && (
                                        <span className="text-warning font-weight-bold mr-2">
                                          <i className="fas fa-exclamation-circle"></i>{" "}
                                          รอท่านอนุมัติ {sub.pendingForYouCount}{" "}
                                          ข้อ
                                        </span>
                                      )}
                                      {(sub.approvedCount || 0) > 0 && (
                                        <span className="text-success mr-2">
                                          <i className="fas fa-check"></i>{" "}
                                          อนุมัติแล้ว {sub.approvedCount}
                                        </span>
                                      )}
                                      {(sub.rejectedCount || 0) > 0 && (
                                        <span className="text-danger">
                                          <i className="fas fa-undo"></i>{" "}
                                          ส่งกลับ {sub.rejectedCount}
                                        </span>
                                      )}
                                    </div>
                                  </div>

                                  {/* Action Button: Approve All for this subordinate */}
                                  {hasPendingForYou && (
                                    <button
                                      type="button"
                                      className="btn btn-success btn-sm shadow-sm text-nowrap font-weight-bold"
                                      disabled={submitting}
                                      onClick={() =>
                                        handleApproveAllForSubordinate(
                                          sub.userName,
                                          sub.fullName,
                                          sub.pendingForYouCount
                                        )
                                      }
                                    >
                                      <i className="fas fa-check-double mr-1"></i>{" "}
                                      อนุมัติทั้งหมด ({sub.pendingForYouCount}{" "}
                                      ข้อ)
                                    </button>
                                  )}
                                </div>
                              </div>
                            </div>

                            {/* KPI Assignment Table for this Subordinate */}
                            <div className="card-body p-0">
                              <div className="table-responsive">
                                <table
                                  className="table table-hover table-bordered mb-0"
                                  style={{ fontSize: "13px" }}
                                >
                                  <thead
                                    className="thead-light"
                                    style={{ backgroundColor: "#f8fafc" }}
                                  >
                                    <tr>
                                      <th
                                        style={{
                                          width: "40px",
                                          textAlign: "center"
                                        }}
                                      >
                                        #
                                      </th>
                                      <th style={{ minWidth: "220px" }}>
                                        ตัวชี้วัด (KPI)
                                      </th>
                                      <th
                                        style={{
                                          width: "80px",
                                          textAlign: "center"
                                        }}
                                      >
                                        น้ำหนัก
                                      </th>
                                      <th
                                        style={{
                                          width: "100px",
                                          textAlign: "center"
                                        }}
                                      >
                                        เป้าหมาย
                                      </th>
                                      <th style={{ minWidth: "300px" }}>
                                        สายการอนุมัติ (Sequential Approval)
                                      </th>
                                      <th
                                        style={{
                                          width: "140px",
                                          textAlign: "center"
                                        }}
                                      >
                                        การดำเนินการ
                                      </th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {(Array.isArray(sub?.assignments)
                                      ? sub.assignments
                                      : []
                                    ).map((item, aIdx) => {
                                      const canApprove =
                                        item.canCurrentBossApprove;
                                      const isApproved =
                                        item.assignmentStatus === 2;
                                      const isRejected =
                                        item.assignmentStatus === 3;

                                      return (
                                        <tr
                                          key={item.assignmentID || aIdx}
                                          className={
                                            canApprove
                                              ? "table-warning"
                                              : isRejected
                                              ? "table-danger"
                                              : ""
                                          }
                                        >
                                          <td className="text-center font-weight-bold align-middle">
                                            {aIdx + 1}
                                          </td>

                                          {/* KPI Name & Code */}
                                          <td className="align-middle">
                                            <div className="d-flex align-items-start">
                                              {item.kpiType === 1 ? (
                                                <span
                                                  className="badge badge-info mr-1"
                                                  style={{ fontSize: "10px" }}
                                                >
                                                  ส่วนกลาง
                                                </span>
                                              ) : (
                                                <span
                                                  className="badge badge-primary mr-1"
                                                  style={{ fontSize: "10px" }}
                                                >
                                                  หน่วยงาน
                                                </span>
                                              )}
                                              <div>
                                                <span className="font-weight-bold text-dark">
                                                  {item.code
                                                    ? `[${item.code}] `
                                                    : ""}
                                                  {item.kpiName}
                                                </span>
                                                {item.planProjectName && (
                                                  <div className="small text-muted">
                                                    <i className="fas fa-folder mr-1"></i>
                                                    {item.planProjectName}
                                                  </div>
                                                )}
                                              </div>
                                            </div>
                                          </td>

                                          {/* Weight */}
                                          <td
                                            className="text-center align-middle font-weight-bold text-primary"
                                            style={{ fontSize: "14px" }}
                                          >
                                            {item.weight != null
                                              ? item.weight.toFixed(2)
                                              : "0.00"}
                                            %
                                          </td>

                                          {/* Target */}
                                          <td className="text-center align-middle">
                                            <span
                                              className="badge badge-pill badge-primary"
                                              style={{ fontSize: "11px" }}
                                            >
                                              ระดับ {item.targetScoreLevel || 3}
                                            </span>
                                            <div className="small text-muted">
                                              {item.unitName || "-"}
                                            </div>
                                          </td>

                                          {/* Sequential Approval Stepper Badges */}
                                          <td className="align-middle">
                                            <div className="d-flex flex-wrap align-items-center mb-1">
                                              {/* Step 1: Department */}
                                              <div className="mr-1 mb-1">
                                                {!item.hasDeptBoss ? (
                                                  <span
                                                    className="badge badge-secondary py-1 px-2"
                                                    title="ไม่มีหัวหน้างานระดับหน่วยงาน (ข้ามระดับนี้อัตโนมัติ)"
                                                  >
                                                    1. หน่วยงาน: -
                                                  </span>
                                                ) : item.deptApprovedBy ? (
                                                  <span
                                                    className="badge badge-success py-1 px-2"
                                                    title={`อนุมัติโดย ${item.deptApprovedFullName ||
                                                      item.deptApprovedBy} เมื่อ ${formatThaiDate(
                                                      item.deptApprovedDate
                                                    )}`}
                                                  >
                                                    <i className="fas fa-check mr-1"></i>
                                                    1. หน.งาน:{" "}
                                                    {item.deptApprovedFullName ||
                                                      item.deptApprovedBy}
                                                  </span>
                                                ) : item.currentRequiredLevel ===
                                                  1 ? (
                                                  <span
                                                    className="badge badge-warning py-1 px-2 font-weight-bold"
                                                    style={{
                                                      color: "#7c2d12",
                                                      backgroundColor: "#fef08a"
                                                    }}
                                                    title="กำลังรอการอนุมัติจากหัวหน้างาน"
                                                  >
                                                    <i className="fas fa-hourglass-start mr-1"></i>
                                                    1. รอ หน.งาน (
                                                    {item.deptBossFullName ||
                                                      item.deptBossUserName ||
                                                      "รอตรวจสอบ"}
                                                    )
                                                  </span>
                                                ) : (
                                                  <span className="badge badge-light border text-muted py-1 px-2">
                                                    1. รอ หน.งาน
                                                  </span>
                                                )}
                                              </div>

                                              <i
                                                className="fas fa-chevron-right text-muted mx-1"
                                                style={{ fontSize: "10px" }}
                                              ></i>

                                              {/* Step 2: GroupWork */}
                                              <div className="mr-1 mb-1">
                                                {!item.hasGroupWorkBoss ? (
                                                  <span
                                                    className="badge badge-secondary py-1 px-2"
                                                    title="ไม่มีหัวหน้ากลุ่มงาน (ข้ามระดับนี้อัตโนมัติ)"
                                                  >
                                                    2. กลุ่มงาน: -
                                                  </span>
                                                ) : item.groupWorkApprovedBy ? (
                                                  <span
                                                    className="badge badge-success py-1 px-2"
                                                    title={`อนุมัติโดย ${item.groupWorkApprovedFullName ||
                                                      item.groupWorkApprovedBy} เมื่อ ${formatThaiDate(
                                                      item.groupWorkApprovedDate
                                                    )}`}
                                                  >
                                                    <i className="fas fa-check mr-1"></i>
                                                    2. หน.กลุ่มงาน:{" "}
                                                    {item.groupWorkApprovedFullName ||
                                                      item.groupWorkApprovedBy}
                                                  </span>
                                                ) : item.currentRequiredLevel ===
                                                  2 ? (
                                                  <span
                                                    className="badge badge-warning py-1 px-2 font-weight-bold"
                                                    style={{
                                                      color: "#7c2d12",
                                                      backgroundColor: "#fef08a"
                                                    }}
                                                    title="กำลังรอการอนุมัติจากหัวหน้ากลุ่มงาน"
                                                  >
                                                    <i className="fas fa-hourglass-half mr-1"></i>
                                                    2. รอ หน.กลุ่มงาน (
                                                    {item.groupWorkBossFullName ||
                                                      item.groupWorkBossUserName ||
                                                      "รอตรวจ"}
                                                    )
                                                  </span>
                                                ) : (
                                                  <span className="badge badge-light border text-muted py-1 px-2">
                                                    2. หน.กลุ่มงาน
                                                  </span>
                                                )}
                                              </div>

                                              <i
                                                className="fas fa-chevron-right text-muted mx-1"
                                                style={{ fontSize: "10px" }}
                                              ></i>

                                              {/* Step 3: Mission */}
                                              <div className="mr-1 mb-1">
                                                {!item.hasMissionBoss ? (
                                                  <span
                                                    className="badge badge-secondary py-1 px-2"
                                                    title="ไม่มีหัวหน้าภารกิจ (ข้ามระดับนี้อัตโนมัติ)"
                                                  >
                                                    3. ภารกิจ: -
                                                  </span>
                                                ) : item.missionApprovedBy ? (
                                                  <span
                                                    className="badge badge-success py-1 px-2"
                                                    title={`อนุมัติโดย ${item.missionApprovedFullName ||
                                                      item.missionApprovedBy} เมื่อ ${formatThaiDate(
                                                      item.missionApprovedDate
                                                    )}`}
                                                  >
                                                    <i className="fas fa-check mr-1"></i>
                                                    3. หน.ภารกิจ:{" "}
                                                    {item.missionApprovedFullName ||
                                                      item.missionApprovedBy}
                                                  </span>
                                                ) : item.currentRequiredLevel ===
                                                  3 ? (
                                                  <span
                                                    className="badge badge-warning py-1 px-2 font-weight-bold"
                                                    style={{
                                                      color: "#7c2d12",
                                                      backgroundColor: "#fef08a"
                                                    }}
                                                    title="กำลังรอการอนุมัติจากหัวหน้าภารกิจ"
                                                  >
                                                    <i className="fas fa-hourglass-half mr-1"></i>
                                                    3. รอ หน.ภารกิจ (
                                                    {item.missionBossFullName ||
                                                      item.missionBossUserName ||
                                                      "รอตรวจ"}
                                                    )
                                                  </span>
                                                ) : (
                                                  <span className="badge badge-light border text-muted py-1 px-2">
                                                    3. หน.ภารกิจ
                                                  </span>
                                                )}
                                              </div>
                                            </div>

                                            {/* Status Message / Remark */}
                                            <div>
                                              {isApproved && (
                                                <span className="text-success font-weight-bold small">
                                                  <i className="fas fa-check-circle mr-1"></i>{" "}
                                                  อนุมัติครบทุกระดับเรียบร้อย
                                                </span>
                                              )}
                                              {isRejected && (
                                                <div className="text-danger small font-weight-bold">
                                                  <i className="fas fa-exclamation-triangle mr-1"></i>{" "}
                                                  ส่งกลับแก้ไข:{" "}
                                                  <span className="font-weight-normal">
                                                    {item.assignmentRemark ||
                                                      "โปรดปรับปรุงตัวชี้วัด"}
                                                  </span>
                                                </div>
                                              )}
                                              {!isRejected &&
                                                item.subordinateClarification && (
                                                  <div
                                                    className="p-2 rounded mb-1 border text-left"
                                                    style={{
                                                      backgroundColor:
                                                        "#f0fdf4",
                                                      borderColor: "#86efac",
                                                      color: "#166534",
                                                      fontSize: "12px"
                                                    }}
                                                  >
                                                    <div className="font-weight-bold mb-1">
                                                      <i className="fas fa-reply text-success mr-1"></i>
                                                      การตอบคำชี้แจงจากผู้รับการประเมิน:
                                                      {item.subordinateClarificationDate && (
                                                        <span
                                                          className="font-weight-normal text-muted ml-1"
                                                          style={{
                                                            fontSize: "11px"
                                                          }}
                                                        >
                                                          (
                                                          {formatThaiDate(
                                                            item.subordinateClarificationDate
                                                          )}
                                                          )
                                                        </span>
                                                      )}
                                                    </div>
                                                    <div
                                                      className="text-dark pl-2"
                                                      style={{
                                                        borderLeft:
                                                          "2px solid #22c55e",
                                                        whiteSpace: "pre-wrap"
                                                      }}
                                                    >
                                                      {
                                                        item.subordinateClarification
                                                      }
                                                    </div>
                                                  </div>
                                                )}
                                              {canApprove && (
                                                <div className="text-primary font-weight-bold small">
                                                  <i className="fas fa-bell mr-1 text-warning"></i>{" "}
                                                  ถึงคิวที่ท่านต้องพิจารณาอนุมัติ
                                                </div>
                                              )}
                                            </div>
                                          </td>

                                          {/* Action Buttons */}
                                          <td className="text-center align-middle">
                                            <div className="d-flex justify-content-center align-items-center">
                                              {/* View Detail Modal Button */}
                                              <button
                                                type="button"
                                                className="btn btn-outline-info btn-sm px-2 py-1 mr-1"
                                                onClick={() =>
                                                  setSelectedKpiForDetail(item)
                                                }
                                                title="ดูเกณฑ์ 5 ระดับ (รอบ 6 และ 12 เดือน) และรายละเอียดตัวชี้วัด"
                                              >
                                                <i className="fas fa-eye"></i>
                                              </button>

                                              {/* Approve & Reject buttons if user can approve this item */}
                                              {canApprove && (
                                                <>
                                                  <button
                                                    type="button"
                                                    className="btn btn-success btn-sm px-2 py-1 mr-1"
                                                    disabled={submitting}
                                                    onClick={() =>
                                                      handleApproveOne(
                                                        item,
                                                        sub.fullName
                                                      )
                                                    }
                                                    title="อนุมัติตัวชี้วัดข้อนี้"
                                                  >
                                                    <i className="fas fa-check"></i>
                                                  </button>
                                                  <button
                                                    type="button"
                                                    className="btn btn-outline-danger btn-sm px-2 py-1"
                                                    disabled={submitting}
                                                    onClick={() =>
                                                      handleOpenRejectModal(
                                                        item
                                                      )
                                                    }
                                                    title="ส่งกลับแก้ไขตัวชี้วัดข้อนี้"
                                                  >
                                                    <i className="fas fa-undo"></i>
                                                  </button>
                                                </>
                                              )}
                                            </div>
                                          </td>
                                        </tr>
                                      );
                                    })}
                                  </tbody>
                                </table>
                              </div>
                            </div>
                          </div>
                        );
                      })()}
                    </div>
                  )}
                </div>
              </div>
            </CardBody>
          </Card>
        </Col>
      </Grid>

      {/* ========================================================================= */}
      {/* Modal 1: ดูรายละเอียดตัวชี้วัด & เกณฑ์คะแนน 5 ระดับ (6M / 12M) */}
      {/* ========================================================================= */}
      <Modal
        isOpen={!!selectedKpiForDetail}
        onRequestClose={() => setSelectedKpiForDetail(null)}
        style={{
          overlay: styles.modalOverlay,
          content: styles.modalContent
        }}
        contentLabel="รายละเอียดตัวชี้วัดและเกณฑ์คะแนน"
      >
        {selectedKpiForDetail &&
          (() => {
            const allAssignees = selectedKpiForDetail.assignedUsers || [];
            const targetUserName =
              selectedKpiForDetail.userName || selectedSubordinate?.userName;
            const currentAssignee = allAssignees.find(
              u =>
                u.userName &&
                targetUserName &&
                u.userName.toLowerCase() === targetUserName.toLowerCase()
            );
            const evalFullName =
              currentAssignee?.fullName ||
              selectedKpiForDetail.fullName ||
              selectedSubordinate?.fullName ||
              targetUserName;
            const evalNickName =
              currentAssignee?.nickName || selectedSubordinate?.nickName || "";
            const evalPosition =
              currentAssignee?.positionName ||
              selectedSubordinate?.positionName ||
              "";

            // ผู้ร่วมวัด (ไม่รวมคนที่เป็นเจ้าของตัวชี้วัดที่กำลังอนุมัติ)
            const coAssignees = allAssignees.filter(
              u =>
                u.userName &&
                targetUserName &&
                u.userName.toLowerCase() !== targetUserName.toLowerCase()
            );

            const totalCoWeight = coAssignees.reduce(
              (acc, cur) => acc + (cur.weight || 0),
              0
            );

            return (
              <div>
                {/* Modal Header */}
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "flex-start",
                    borderBottom: "2px solid #0284c7",
                    paddingBottom: "12px",
                    marginBottom: "16px"
                  }}
                >
                  <div>
                    {/* แสดงว่าผู้รับการประเมินเป็นใคร */}
                    <div className="d-flex align-items-center flex-wrap mb-2">
                      <div
                        className="d-inline-flex align-items-center bg-primary text-white rounded px-2 py-1 mr-2 shadow-sm"
                        style={{ fontSize: "13px" }}
                      >
                        <img
                          src={`https://e.trangskin.go.th/avatar/${targetUserName}.jpg`}
                          alt={evalFullName}
                          className="rounded-circle mr-2 border border-white"
                          style={{
                            width: "24px",
                            height: "24px",
                            objectFit: "cover"
                          }}
                          onError={e => {
                            e.target.onerror = null;
                            e.target.style.display = "none";
                          }}
                        />
                        <i className="fas fa-user-check mr-1"></i>
                        <span>
                          ผู้รับการประเมิน: <strong>{evalFullName}</strong>
                          {evalNickName ? ` (${evalNickName})` : ""}
                        </span>
                        {evalPosition && (
                          <span
                            className="ml-1 opacity-75"
                            style={{ fontSize: "11px" }}
                          >
                            • {evalPosition}
                          </span>
                        )}
                      </div>
                      <span
                        className="badge badge-info px-2 py-1 mr-2"
                        style={{ fontSize: "12px" }}
                      >
                        สัดส่วนที่ได้รับ:{" "}
                        {selectedKpiForDetail.weight != null
                          ? selectedKpiForDetail.weight.toFixed(2)
                          : "0.00"}
                        %
                      </span>
                      <span className="small text-muted">
                        รอบการประเมิน: {selectedPeriodObj?.periodName || "-"} |
                        ประเภท:{" "}
                        {selectedKpiForDetail.kpiType === 1
                          ? "ตัวชี้วัดส่วนกลาง"
                          : "ตัวชี้วัดระดับหน่วยงาน"}
                      </span>
                    </div>

                    <h4
                      style={{
                        margin: 0,
                        color: "#0284c7",
                        fontWeight: "bold"
                      }}
                    >
                      <i className="fas fa-bullseye mr-2"></i>
                      อนุมัติตัวชี้วัดของ {evalFullName}
                      {evalNickName ? ` (${evalNickName})` : ""}:{" "}
                      {selectedKpiForDetail.code
                        ? `[${selectedKpiForDetail.code}] `
                        : ""}
                      {selectedKpiForDetail.kpiName}
                    </h4>
                  </div>
                  <button
                    type="button"
                    className="close"
                    style={{ fontSize: "28px" }}
                    onClick={() => setSelectedKpiForDetail(null)}
                  >
                    <span>&times;</span>
                  </button>
                </div>

                {/* ประวัติการส่งกลับแก้ไขและการตอบคำชี้แจง */}
                {(selectedKpiForDetail.assignmentRemark ||
                  selectedKpiForDetail.subordinateClarification) && (
                  <div
                    className="card mb-3 border-0 shadow-sm"
                    style={{
                      border: "1px solid #fed7aa",
                      borderRadius: "8px",
                      overflow: "hidden"
                    }}
                  >
                    <div
                      className="card-header py-2 px-3 d-flex align-items-center"
                      style={{ backgroundColor: "#fff7ed" }}
                    >
                      <i className="fas fa-comments text-warning mr-2"></i>
                      <span
                        className="font-weight-bold text-dark"
                        style={{ fontSize: "14px" }}
                      >
                        ประวัติการส่งกลับแก้ไขและการตอบคำชี้แจง
                      </span>
                    </div>
                    <div className="card-body p-3 bg-white">
                      {selectedKpiForDetail.assignmentRemark && (
                        <div className="mb-2 p-2 rounded bg-light border">
                          <span className="badge badge-danger mr-2">
                            <i className="fas fa-undo-alt mr-1"></i>{" "}
                            คำชี้แจงจากหัวหน้า
                          </span>
                          <span
                            className="text-dark"
                            style={{ fontSize: "13px" }}
                          >
                            {selectedKpiForDetail.assignmentRemark}
                          </span>
                        </div>
                      )}
                      {selectedKpiForDetail.subordinateClarification && (
                        <div
                          className="p-2 rounded"
                          style={{
                            backgroundColor: "#f0fdf4",
                            border: "1px solid #bbf7d0"
                          }}
                        >
                          <div
                            className="font-weight-bold text-success mb-1"
                            style={{ fontSize: "13px" }}
                          >
                            <i className="fas fa-reply mr-1"></i>{" "}
                            คำชี้แจงตอบกลับจาก {evalFullName}:
                            {selectedKpiForDetail.subordinateClarificationDate && (
                              <span
                                className="text-muted ml-1 font-weight-normal"
                                style={{ fontSize: "11px" }}
                              >
                                (
                                {formatThaiDate(
                                  selectedKpiForDetail.subordinateClarificationDate
                                )}
                                )
                              </span>
                            )}
                          </div>
                          <div
                            className="text-dark pl-2"
                            style={{
                              borderLeft: "3px solid #22c55e",
                              whiteSpace: "pre-wrap",
                              fontSize: "13px"
                            }}
                          >
                            {selectedKpiForDetail.subordinateClarification}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* General Information */}
                <div className="row mb-3">
                  <div className="col-md-6 mb-2">
                    <div className="p-3 bg-light rounded border h-100">
                      <div className="font-weight-bold text-dark mb-2">
                        ข้อมูลทั่วไป
                      </div>
                      <div className="small mb-1">
                        <span className="text-muted">แผนงาน / โครงการ: </span>
                        <span className="font-weight-bold">
                          {selectedKpiForDetail.planProjectName || "-"}
                        </span>
                      </div>
                      <div className="small mb-1">
                        <span className="text-muted">
                          ความสอดคล้องเชิงยุทธศาสตร์:{" "}
                        </span>
                        <span className="font-weight-bold">
                          {selectedKpiForDetail.strategicAlignment || "-"}
                        </span>
                      </div>
                      <div className="small mb-1">
                        <span className="text-muted">หน่วยวัด: </span>
                        <span className="font-weight-bold">
                          {selectedKpiForDetail.unitName || "-"}
                        </span>
                      </div>
                      <div className="small mb-1">
                        <span className="text-muted">
                          ค่าเป้าหมาย (Target Level):{" "}
                        </span>
                        <span className="badge badge-primary">
                          ระดับ {selectedKpiForDetail.targetScoreLevel || 3}
                        </span>
                      </div>
                      <div className="small mb-1">
                        <span className="text-muted">น้ำหนัก: </span>
                        <span className="badge badge-info">
                          {selectedKpiForDetail.weight != null
                            ? selectedKpiForDetail.weight.toFixed(2)
                            : "0.00"}
                          %
                        </span>
                      </div>
                    </div>
                  </div>

                  <div className="col-md-6 mb-2">
                    <div className="p-3 bg-light rounded border h-100">
                      <div className="font-weight-bold text-dark mb-2">
                        คำอธิบายและสูตรการคำนวณ
                      </div>
                      <div className="small mb-2">
                        <span className="text-muted">คำอธิบายตัวชี้วัด: </span>
                        <div>
                          {selectedKpiForDetail.description || "ไม่มีคำอธิบาย"}
                        </div>
                      </div>
                      {selectedKpiForDetail.calculationFormula && (
                        <div className="small mb-1">
                          <span className="text-muted">สูตรการคำนวณ: </span>
                          <div className="p-2 bg-white rounded border">
                            {selectedKpiForDetail.calculationFormula}
                          </div>
                        </div>
                      )}
                      {selectedKpiForDetail.baselineData && (
                        <div className="small mb-1">
                          <span className="text-muted">
                            ข้อมูลเกณฑ์มาตรฐาน (Baseline Data):{" "}
                          </span>
                          <div className="text-dark">
                            {selectedKpiForDetail.baselineData}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* รายชื่อผู้ร่วมรับผิดชอบตัวชี้วัดนี้ (ไม่รวมผู้รับการประเมิน) */}
                <div
                  className="card mb-3 border-0 shadow-sm"
                  style={{ border: "1px solid #e2e8f0", borderRadius: "8px" }}
                >
                  <div className="card-header bg-light py-2 px-3 border-bottom d-flex justify-content-between align-items-center">
                    <div
                      className="font-weight-bold text-dark"
                      style={{ fontSize: "14px" }}
                    >
                      <i className="fas fa-users text-primary mr-2"></i>
                      ผู้ร่วมรับผิดชอบตัวชี้วัดนี้ ({coAssignees.length} คน)
                    </div>
                    <div className="small text-muted">
                      {coAssignees.length > 0 ? (
                        <>
                          สัดส่วนผู้ร่วมวัดรวม:{" "}
                          <strong className="text-dark">
                            {totalCoWeight.toFixed(2)}%
                          </strong>{" "}
                          | ผู้รับการประเมิน ({evalFullName}):{" "}
                          <strong className="text-primary">
                            {selectedKpiForDetail.weight != null
                              ? selectedKpiForDetail.weight.toFixed(2)
                              : "0.00"}
                            %
                          </strong>
                        </>
                      ) : (
                        <span>
                          ผู้รับการประเมินปฏิบัติงานเดี่ยว (
                          {selectedKpiForDetail.weight != null
                            ? selectedKpiForDetail.weight.toFixed(2)
                            : "0.00"}
                          %)
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="card-body p-0">
                    {coAssignees.length === 0 ? (
                      <div className="p-3 text-muted text-center small">
                        <i className="fas fa-info-circle mr-1 text-info"></i>
                        ไม่มีผู้ร่วมวัดอื่นในตัวชี้วัดนี้ ({evalFullName}{" "}
                        ได้รับมอบหมายเป็นผู้รับผิดชอบเดี่ยว 100%
                        ของภาระงานตัวชี้วัดนี้)
                      </div>
                    ) : (
                      <div className="table-responsive mb-0">
                        <table
                          className="table table-sm table-hover mb-0"
                          style={{ fontSize: "13px" }}
                        >
                          <thead className="thead-light">
                            <tr>
                              <th
                                style={{ width: "40px" }}
                                className="text-center"
                              >
                                #
                              </th>
                              <th>ชื่อผู้ร่วมรับผิดชอบ / ตำแหน่ง</th>
                              <th style={{ width: "160px" }}>หน่วยงาน</th>
                              <th
                                style={{ width: "110px" }}
                                className="text-center"
                              >
                                สัดส่วน (%)
                              </th>
                              <th
                                style={{ width: "110px" }}
                                className="text-center"
                              >
                                สถานะ
                              </th>
                            </tr>
                          </thead>
                          <tbody>
                            {coAssignees.map((u, idx) => (
                              <tr key={u.assignmentID || u.userName || idx}>
                                <td className="text-center align-middle text-muted">
                                  {idx + 1}
                                </td>
                                <td className="align-middle">
                                  <div className="d-flex align-items-center">
                                    <img
                                      src={`https://e.trangskin.go.th/avatar/${u.userName}.jpg`}
                                      alt={u.fullName || u.userName}
                                      className="rounded-circle mr-2"
                                      style={{
                                        width: "32px",
                                        height: "32px",
                                        objectFit: "cover"
                                      }}
                                      onError={e => {
                                        e.target.onerror = null;
                                        e.target.style.display = "none";
                                        if (e.target.nextSibling) {
                                          e.target.nextSibling.style.display =
                                            "flex";
                                        }
                                      }}
                                    />
                                    <div
                                      className="rounded-circle align-items-center justify-content-center text-white font-weight-bold mr-2"
                                      style={{
                                        width: "32px",
                                        height: "32px",
                                        backgroundColor: "#0284c7",
                                        fontSize: "13px",
                                        display: "none",
                                        flexShrink: 0
                                      }}
                                    >
                                      {u.fullName
                                        ? u.fullName.charAt(0)
                                        : u.userName
                                        ? u.userName.charAt(0).toUpperCase()
                                        : "U"}
                                    </div>
                                    <div>
                                      <div className="font-weight-bold text-dark">
                                        {u.fullName || u.userName}
                                        {u.nickName ? (
                                          <span className="text-muted font-weight-normal">
                                            {" "}
                                            ({u.nickName})
                                          </span>
                                        ) : (
                                          ""
                                        )}
                                      </div>
                                      <div className="text-muted small">
                                        {u.positionName || "-"}
                                      </div>
                                    </div>
                                  </div>
                                </td>
                                <td className="align-middle text-muted small">
                                  {u.departmentName || "-"}
                                </td>
                                <td className="align-middle text-center">
                                  <span
                                    className="badge badge-info px-2 py-1 font-weight-bold"
                                    style={{ fontSize: "12px" }}
                                  >
                                    {u.weight != null
                                      ? u.weight.toFixed(2)
                                      : "0.00"}
                                    %
                                  </span>
                                </td>
                                <td className="align-middle text-center">
                                  {u.assignmentStatus === 2 ? (
                                    <span className="badge badge-success px-2 py-1">
                                      อนุมัติแล้ว
                                    </span>
                                  ) : u.assignmentStatus === 3 ? (
                                    <span className="badge badge-danger px-2 py-1">
                                      ส่งกลับแก้ไข
                                    </span>
                                  ) : u.assignmentStatus === 1 ? (
                                    <span className="badge badge-warning px-2 py-1 text-white">
                                      รออนุมัติ
                                    </span>
                                  ) : (
                                    <span className="badge badge-secondary px-2 py-1">
                                      ร่าง
                                    </span>
                                  )}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                </div>

                {/* Criteria Tab Switcher */}
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <h5 className="font-weight-bold text-dark mb-0">
                    <i className="fas fa-list-ol mr-2 text-primary"></i>{" "}
                    เกณฑ์การให้คะแนน 5 ระดับ (Scoring Criteria)
                  </h5>
                  <div
                    className="btn-group btn-group-toggle"
                    data-toggle="buttons"
                  >
                    <button
                      type="button"
                      className={`btn btn-sm ${
                        activeCriteriaTab === "both"
                          ? "btn-primary"
                          : "btn-outline-primary"
                      }`}
                      onClick={() => setActiveCriteriaTab("both")}
                    >
                      เปรียบเทียบทั้ง 2 รอบ
                    </button>
                    <button
                      type="button"
                      className={`btn btn-sm ${
                        activeCriteriaTab === "6m"
                          ? "btn-primary"
                          : "btn-outline-primary"
                      }`}
                      onClick={() => setActiveCriteriaTab("6m")}
                    >
                      รอบ 6 เดือน
                    </button>
                    <button
                      type="button"
                      className={`btn btn-sm ${
                        activeCriteriaTab === "12m"
                          ? "btn-primary"
                          : "btn-outline-primary"
                      }`}
                      onClick={() => setActiveCriteriaTab("12m")}
                    >
                      รอบ 12 เดือน
                    </button>
                  </div>
                </div>

                {/* Criteria Cards */}
                <div className="row">
                  {/* รอบ 6 เดือน */}
                  {(activeCriteriaTab === "both" ||
                    activeCriteriaTab === "6m") && (
                    <div
                      className={
                        activeCriteriaTab === "both" ? "col-md-6" : "col-12"
                      }
                    >
                      <div className="card border-primary mb-3">
                        <div className="card-header bg-primary text-white py-2 font-weight-bold">
                          <i className="fas fa-calendar-alt mr-1"></i>{" "}
                          เกณฑ์การประเมินรอบ 6 เดือน
                        </div>
                        <div className="card-body p-3">
                          {[1, 2, 3, 4, 5].map(lvl => {
                            const text =
                              selectedKpiForDetail[`criteriaLevel${lvl}`];
                            return (
                              <div
                                key={`6m-${lvl}`}
                                className="p-2 mb-2 rounded border-left"
                                style={{
                                  backgroundColor:
                                    lvl ===
                                    selectedKpiForDetail.targetScoreLevel
                                      ? "#eff6ff"
                                      : "#f8fafc",
                                  borderLeft: `4px solid ${
                                    lvl ===
                                    selectedKpiForDetail.targetScoreLevel
                                      ? "#0284c7"
                                      : "#cbd5e1"
                                  }`
                                }}
                              >
                                <div className="d-flex justify-content-between">
                                  <span
                                    className="font-weight-bold"
                                    style={{ color: "#0369a1" }}
                                  >
                                    ระดับ {lvl} คะแนน
                                  </span>
                                  {lvl ===
                                    selectedKpiForDetail.targetScoreLevel && (
                                    <span className="badge badge-primary">
                                      เป้าหมาย
                                    </span>
                                  )}
                                </div>
                                <div
                                  className="small text-dark mt-1"
                                  style={{ whiteSpace: "pre-wrap" }}
                                >
                                  {text || (
                                    <span className="text-muted italic">
                                      ไม่ได้ระบุเกณฑ์
                                    </span>
                                  )}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    </div>
                  )}

                  {/* รอบ 12 เดือน */}
                  {(activeCriteriaTab === "both" ||
                    activeCriteriaTab === "12m") && (
                    <div
                      className={
                        activeCriteriaTab === "both" ? "col-md-6" : "col-12"
                      }
                    >
                      <div className="card border-success mb-3">
                        <div className="card-header bg-success text-white py-2 font-weight-bold">
                          <i className="fas fa-calendar-check mr-1"></i>{" "}
                          เกณฑ์การประเมินรอบ 12 เดือน
                        </div>
                        <div className="card-body p-3">
                          {[1, 2, 3, 4, 5].map(lvl => {
                            const text =
                              selectedKpiForDetail[`criteria12M_Level${lvl}`];
                            return (
                              <div
                                key={`12m-${lvl}`}
                                className="p-2 mb-2 rounded border-left"
                                style={{
                                  backgroundColor:
                                    lvl ===
                                    selectedKpiForDetail.targetScoreLevel
                                      ? "#f0fdf4"
                                      : "#f8fafc",
                                  borderLeft: `4px solid ${
                                    lvl ===
                                    selectedKpiForDetail.targetScoreLevel
                                      ? "#16a34a"
                                      : "#cbd5e1"
                                  }`
                                }}
                              >
                                <div className="d-flex justify-content-between">
                                  <span
                                    className="font-weight-bold"
                                    style={{ color: "#15803d" }}
                                  >
                                    ระดับ {lvl} คะแนน
                                  </span>
                                  {lvl ===
                                    selectedKpiForDetail.targetScoreLevel && (
                                    <span className="badge badge-success">
                                      เป้าหมาย
                                    </span>
                                  )}
                                </div>
                                <div
                                  className="small text-dark mt-1"
                                  style={{ whiteSpace: "pre-wrap" }}
                                >
                                  {text || (
                                    <span className="text-muted italic">
                                      ไม่ได้ระบุเกณฑ์
                                    </span>
                                  )}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                {/* Modal Footer */}
                <div className="d-flex justify-content-end border-top pt-3 mt-3">
                  <Button
                    color="secondary"
                    onClick={() => setSelectedKpiForDetail(null)}
                  >
                    ปิดหน้าต่าง
                  </Button>
                </div>
              </div>
            );
          })()}
      </Modal>

      {/* ========================================================================= */}
      {/* Modal 2: ส่งกลับแก้ไข (Reject Assignment with Remark) */}
      {/* ========================================================================= */}
      <Modal
        isOpen={!!rejectingItem}
        onRequestClose={() => setRejectingItem(null)}
        style={{
          overlay: styles.modalOverlay,
          content: styles.rejectModalContent
        }}
        contentLabel="ส่งกลับแก้ไขตัวชี้วัด"
      >
        {rejectingItem && (
          <div>
            <div className="d-flex justify-content-between align-items-center border-bottom pb-2 mb-3">
              <h5 className="font-weight-bold text-danger mb-0">
                <i className="fas fa-undo-alt mr-2"></i> ส่งกลับแก้ไขตัวชี้วัด
              </h5>
              <button
                type="button"
                className="close"
                onClick={() => setRejectingItem(null)}
              >
                <span>&times;</span>
              </button>
            </div>

            <div className="mb-3">
              <div className="font-weight-bold text-dark">
                {rejectingItem.kpiName}
              </div>
              <div className="small text-muted">
                ผู้รับผิดชอบ: {rejectingItem.userName}
              </div>
            </div>

            <div className="form-group">
              <label className="font-weight-bold text-dark">
                ระบุเหตุผลหรือข้อเสนอแนะในการปรับปรุงแก้ไข{" "}
                <span className="text-danger">*</span>
              </label>
              <textarea
                className="form-control"
                rows={4}
                placeholder="เช่น ปรับลด/เพิ่มน้ำหนักตัวชี้วัด, แก้ไขเกณฑ์ระดับ 3 ให้สอดคล้องกับภาระงาน..."
                value={rejectRemark}
                onChange={e => setRejectRemark(e.target.value)}
              ></textarea>
            </div>

            {/* ตัวเลือกการแจ้งเตือนทาง Telegram */}
            <div
              className="p-3 mb-3 rounded"
              style={{
                backgroundColor: "#f0fdf4",
                border: "1px solid #bbf7d0"
              }}
            >
              <div className="font-weight-bold text-dark d-flex align-items-center mb-2">
                <i className="fab fa-telegram-plane text-primary fa-lg mr-2"></i>
                <span>การแจ้งเตือนผ่าน Telegram</span>
              </div>
              <div className="small text-muted mb-2">
                ระบบจะส่งข้อความแจ้งเตือนพร้อมเหตุผลผ่าน Telegram
                โปรดเลือกกลุ่มเป้าหมายที่ต้องการแจ้งเตือน:
              </div>

              {/* Radio 1: แจ้งเตือนเฉพาะคนนี้ */}
              <div className="custom-control custom-radio mb-2">
                <input
                  type="radio"
                  id="notifyScopeSingle"
                  name="notifyScope"
                  className="custom-control-input"
                  checked={notifyScope === "single"}
                  onChange={() => setNotifyScope("single")}
                />
                <label
                  className="custom-control-label small font-weight-bold text-dark"
                  htmlFor="notifyScopeSingle"
                  style={{ cursor: "pointer" }}
                >
                  แจ้งเตือนเฉพาะคนนี้:{" "}
                  <span className="text-primary font-weight-bold">
                    {rejectingItem.fullName || rejectingItem.userName}
                  </span>{" "}
                  (
                  {rejectingItem.weight != null
                    ? rejectingItem.weight.toFixed(2)
                    : 0}
                  %)
                </label>
              </div>

              {/* Radio 2: แจ้งเตือนทุกคนที่ได้รับตัวชี้วัดเดียวกันนี้ */}
              <div className="custom-control custom-radio">
                <input
                  type="radio"
                  id="notifyScopeAll"
                  name="notifyScope"
                  className="custom-control-input"
                  checked={notifyScope === "all"}
                  onChange={() => setNotifyScope("all")}
                  disabled={
                    !rejectingItem.assignedUsers ||
                    rejectingItem.assignedUsers.length <= 1
                  }
                />
                <label
                  className={`custom-control-label small font-weight-bold ${
                    !rejectingItem.assignedUsers ||
                    rejectingItem.assignedUsers.length <= 1
                      ? "text-muted"
                      : "text-dark"
                  }`}
                  htmlFor="notifyScopeAll"
                  style={{
                    cursor:
                      !rejectingItem.assignedUsers ||
                      rejectingItem.assignedUsers.length <= 1
                        ? "not-allowed"
                        : "pointer"
                  }}
                >
                  แจ้งเตือนทุกคนที่ได้รับตัวชี้วัดเดียวกันนี้ (
                  {rejectingItem.assignedUsers?.length || 1} คน)
                  {(!rejectingItem.assignedUsers ||
                    rejectingItem.assignedUsers.length <= 1) && (
                    <span className="font-weight-normal text-muted ml-1">
                      (มีผู้รับมอบหมายเพียง 1 คน)
                    </span>
                  )}
                </label>
              </div>

              {/* รายชื่อผู้รับการแจ้งเตือนกรณีเลือกทุกคน */}
              {notifyScope === "all" &&
                rejectingItem.assignedUsers &&
                rejectingItem.assignedUsers.length > 1 && (
                  <div className="mt-2 pl-3 border-left border-success small text-muted">
                    <div className="font-weight-bold mb-1 text-success">
                      รายชื่อผู้ที่จะได้รับการแจ้งเตือน (
                      {rejectingItem.assignedUsers.length} คน):
                    </div>
                    <div className="d-flex flex-wrap" style={{ gap: "4px" }}>
                      {rejectingItem.assignedUsers.map(u => (
                        <span
                          key={u.userName || u.assignmentID}
                          className="badge badge-light border px-2 py-1"
                        >
                          <i className="fas fa-user text-primary mr-1"></i>
                          {u.fullName || u.userName} (
                          {u.weight != null ? u.weight.toFixed(0) : 0}%)
                        </span>
                      ))}
                    </div>
                  </div>
                )}
            </div>

            <div className="d-flex justify-content-end border-top pt-3">
              <button
                type="button"
                className="btn btn-secondary mr-2"
                onClick={() => setRejectingItem(null)}
                disabled={submitting}
              >
                ยกเลิก
              </button>
              <button
                type="button"
                className="btn btn-danger font-weight-bold"
                onClick={handleConfirmReject}
                disabled={submitting || !rejectRemark.trim()}
              >
                {submitting ? (
                  <>
                    <i className="fas fa-spinner fa-spin mr-1"></i>{" "}
                    กำลังบันทึก...
                  </>
                ) : (
                  <>
                    <i className="fas fa-paper-plane mr-1"></i>{" "}
                    ยืนยันส่งกลับแก้ไข
                  </>
                )}
              </button>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default KpiApproval;
